import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Polyline, Polygon, Marker, Tooltip, useMap } from 'react-leaflet';

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './History.css';
// The phone list reuses the live list's card chrome verbatim (.aircraft-card
// and friends), so depend on its stylesheet explicitly rather than by luck of
// the bundle. Nothing here relies on which of the two files wins a tie: the
// History-only rules below are all scoped under a History ancestor.
import './DataTable.css';
import '../assets/flags.css';
import HistoryStatsOverlay from './HistoryStatsOverlay';
import DetectedAircraftType from './DetectedAircraftType';
import { getAirlineLogo, hasAirlineLogo, hasMilitaryLogo, useAirlineLogoMapVersion } from '../utils/airlineLogoMap';
import { getAircraftSpritePosition, getAircraftSizeMultiplier } from '../utils/aircraftSpriteMap';
import { resolveCountryCode } from '../utils/aircraftCountry';
import { getEmergencySquawk } from '../utils/emergencySquawk';
import { getAccessToken } from '../utils/auth';
import { getTrackedAircraftList, getAircraftTracks, getTrackPoints, getMaxDistancePoints, getReceiverHeatmap } from '../utils/httpApi';
import { getTileLayerConfig, getMapFilter, UNIT_SYSTEMS, convertDistance, convertAltitude } from '../utils/mapStyles';
import { useSettings, useColumnConfig } from '../contexts/SettingsContext';
import { filterAndOrderColumns } from '../utils/columnConfig';
import spriteImage from '../assets/BelugaProject/beluga_sprites.png';
import { airlineCodeFromCallsign } from '../utils/callsignAirline';
import { GroundVehicleIcon, MilitaryIcon } from './AircraftTableComponents';
// Shared rule, so the callsign-derived vehicles (DAA*, TWR*, bare TXLU*) are
// treated the same here as on the live map. History previously trusted only the
// backend's persisted groundVehicle flag, which those rows do not carry until
// the backend has seen enough surface-only traffic to set it.
import { isGroundVehicle } from '../utils/groundVehicle';
import { ReceiverChip, ReceiverOptions } from './ReceiverPicker';

// Calculate 10-minute window statistics from aircraft list
// Counts unique aircraft that first appeared (firstSeen) or were last seen in each window
function calculateWindowStats(aircraftList, maxDistancePoints) {
  if (!aircraftList || aircraftList.length === 0) {
    return { windows: [], minAircraft: 0, maxAircraft: 0, maxDistance: 0 };
  }

  // Find the time range of the day
  let minTime = null;
  let maxTime = null;

  aircraftList.forEach(aircraft => {
    if (aircraft.firstSeen) {
      const firstTime = new Date(aircraft.firstSeen).getTime();
      if (minTime === null || firstTime < minTime) minTime = firstTime;
    }
    if (aircraft.lastSeen) {
      const lastTime = new Date(aircraft.lastSeen).getTime();
      if (maxTime === null || lastTime > maxTime) maxTime = lastTime;
    }
  });

  if (minTime === null || maxTime === null) {
    return { windows: [], minAircraft: 0, maxAircraft: 0, maxDistance: 0 };
  }

  // Align to 10-minute boundaries
  const windowSize = 10 * 60 * 1000; // 10 minutes in ms
  const startWindow = Math.floor(minTime / windowSize) * windowSize;
  const endWindow = Math.ceil(maxTime / windowSize) * windowSize;

  const windows = [];
  let globalMinAircraft = Infinity;
  let globalMaxAircraft = 0;
  let globalMaxDistance = 0;

  // Create max distance lookup by timestamp
  const distanceByWindow = {};
  if (maxDistancePoints && maxDistancePoints.length > 0) {
    maxDistancePoints.forEach(point => {
      if (point.timestamp && point.distanceKm) {
        const pointTime = new Date(point.timestamp).getTime();
        const windowStart = Math.floor(pointTime / windowSize) * windowSize;
        if (!distanceByWindow[windowStart] || point.distanceKm > distanceByWindow[windowStart]) {
          distanceByWindow[windowStart] = point.distanceKm;
        }
        if (point.distanceKm > globalMaxDistance) {
          globalMaxDistance = point.distanceKm;
        }
      }
    });
  }

  // Pre-calculate which window each aircraft's firstSeen and lastSeen falls into
  const firstSeenByWindow = {};
  const lastSeenByWindow = {};

  aircraftList.forEach(aircraft => {
    if (aircraft.firstSeen) {
      const firstTime = new Date(aircraft.firstSeen).getTime();
      const windowStart = Math.floor(firstTime / windowSize) * windowSize;
      firstSeenByWindow[windowStart] = (firstSeenByWindow[windowStart] || 0) + 1;
    }
    if (aircraft.lastSeen) {
      const lastTime = new Date(aircraft.lastSeen).getTime();
      const windowStart = Math.floor(lastTime / windowSize) * windowSize;
      lastSeenByWindow[windowStart] = (lastSeenByWindow[windowStart] || 0) + 1;
    }
  });

  // Track cumulative aircraft (arrived minus departed)
  let cumulativeCount = 0;

  for (let windowStart = startWindow; windowStart <= endWindow; windowStart += windowSize) {
    const newArrivals = firstSeenByWindow[windowStart] || 0;
    const departures = lastSeenByWindow[windowStart] || 0;

    // Low: count at start of window (before arrivals)
    const lowCount = cumulativeCount;

    // Add new arrivals
    cumulativeCount += newArrivals;

    // High: count after arrivals (before departures)
    const highCount = cumulativeCount;

    // Process departures for next window
    cumulativeCount -= departures;

    if (lowCount < globalMinAircraft) globalMinAircraft = lowCount;
    if (highCount > globalMaxAircraft) globalMaxAircraft = highCount;

    const windowMaxDist = distanceByWindow[windowStart] || 0;

    windows.push({
      time: windowStart,
      timeLabel: new Date(windowStart).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }),
      high: highCount,
      low: lowCount,
      maxDistance: windowMaxDist
    });
  }

  if (globalMinAircraft === Infinity) globalMinAircraft = 0;

  return {
    windows,
    minAircraft: globalMinAircraft,
    maxAircraft: globalMaxAircraft,
    maxDistance: globalMaxDistance
  };
}

// Activity Chart Component using inline SVG - lightweight, theme-aware, no external deps
function ActivityChart({ windowStats, unitSystem }) {
  if (!windowStats.windows || windowStats.windows.length === 0) {
    return <div className="chart-placeholder">No activity data available</div>;
  }

  const { windows, maxAircraft, maxDistance } = windowStats;
  const hasDistance = maxDistance > 0;
  const maxVal = maxAircraft || 1;

  // Chart layout
  const viewW = 800;
  const viewH = 140;
  const pad = { top: 8, right: hasDistance ? 42 : 12, bottom: 22, left: 34 };
  const plotW = viewW - pad.left - pad.right;
  const plotH = viewH - pad.top - pad.bottom;

  // Y-axis grid
  const gridCount = 4;
  const gridStep = Math.ceil(maxVal / gridCount);
  const gridValues = Array.from({ length: gridCount + 1 }, (_, i) =>
    Math.min(i * gridStep, maxVal)
  );

  // Bar sizing
  const slotW = plotW / windows.length;
  const barW = Math.max(slotW * 0.7, 2);
  const barOff = slotW * 0.15;

  // X-axis labels: show ~8-10 labels
  const labelInterval = Math.max(1, Math.floor(windows.length / 10));

  // Distance overlay
  const maxDist = hasDistance ? convertDistance(maxDistance, unitSystem) : 0;
  const distPoints = hasDistance
    ? windows
        .map((w, i) => {
          const x = pad.left + (i + 0.5) * slotW;
          const d = convertDistance(w.maxDistance || 0, unitSystem);
          const y = pad.top + plotH - (d / (maxDist || 1)) * plotH;
          return `${x},${y}`;
        })
        .join(' ')
    : '';

  return (
    <div className="activity-chart-wrapper">
      <svg
        className="activity-chart-svg"
        viewBox={`0 0 ${viewW} ${viewH}`}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Grid lines + Y-axis labels */}
        {gridValues.map((val, i) => {
          const y = pad.top + plotH - (val / maxVal) * plotH;
          return (
            <g key={`g${i}`}>
              <line
                x1={pad.left} y1={y}
                x2={viewW - pad.right} y2={y}
                className="chart-gridline"
              />
              <text x={pad.left - 4} y={y + 3} className="chart-axis-label" textAnchor="end">
                {val}
              </text>
            </g>
          );
        })}

        {/* Bars */}
        {windows.map((w, i) => {
          const x = pad.left + i * slotW + barOff;
          const highH = (w.high / maxVal) * plotH;
          const lowH = (w.low / maxVal) * plotH;
          return (
            <g key={`b${i}`} className="chart-bar-group">
              <title>{`${w.timeLabel}: ${w.low}\u2013${w.high} aircraft`}</title>
              {lowH > 0 && (
                <rect
                  x={x} y={pad.top + plotH - lowH}
                  width={barW} height={lowH}
                  className="chart-bar-low" rx="1"
                />
              )}
              <rect
                x={x} y={pad.top + plotH - highH}
                width={barW} height={Math.max(highH - lowH, 1)}
                className="chart-bar-high" rx="1"
              />
            </g>
          );
        })}

        {/* Distance line overlay */}
        {hasDistance && distPoints && (
          <polyline points={distPoints} className="chart-dist-line" fill="none" />
        )}

        {/* X-axis labels */}
        {windows.map((w, i) =>
          i % labelInterval === 0 ? (
            <text
              key={`x${i}`}
              x={pad.left + (i + 0.5) * slotW}
              y={viewH - 4}
              className="chart-axis-label"
              textAnchor="middle"
            >
              {w.timeLabel}
            </text>
          ) : null
        )}

        {/* Distance axis labels (right side) */}
        {hasDistance &&
          [0, Math.round(maxDist)].map((val, i) => {
            const y = pad.top + plotH - (val / (maxDist || 1)) * plotH;
            return (
              <text
                key={`d${i}`}
                x={viewW - pad.right + 4} y={y + 3}
                className="chart-dist-label" textAnchor="start"
              >
                {val}
              </text>
            );
          })}
      </svg>
    </div>
  );
}

// Calculate top 10 airlines from aircraft list
function calculateTopAirlines(aircraftList) {
  const airlineMap = {};

  aircraftList.forEach(aircraft => {
    const key = aircraft.airlineCode || aircraft.airlineName || 'Unknown';
    if (key === 'Unknown' && !aircraft.military) return; // Skip unknown non-military

    if (!airlineMap[key]) {
      airlineMap[key] = {
        code: aircraft.airlineCode,
        name: aircraft.airlineName || aircraft.airlineCode || 'Unknown',
        aircraftCount: 0,
        trackCount: 0
      };
    }
    airlineMap[key].aircraftCount++;
    airlineMap[key].trackCount += aircraft.trackCount || 1;
  });

  return Object.values(airlineMap)
    .sort((a, b) => b.aircraftCount - a.aircraftCount)
    .slice(0, 10);
}

// Calculate top 10 aircraft types from aircraft list
function calculateTopTypes(aircraftList) {
  const typeMap = {};

  aircraftList.forEach(aircraft => {
    const key = aircraft.aircraftType || 'Unknown';
    if (key === 'Unknown') return;

    if (!typeMap[key]) {
      typeMap[key] = {
        type: key,
        description: aircraft.aircraftDescription || key,
        aircraftCount: 0,
        trackCount: 0
      };
    }
    typeMap[key].aircraftCount++;
    typeMap[key].trackCount += aircraft.trackCount || 1;
  });

  return Object.values(typeMap)
    .sort((a, b) => b.aircraftCount - a.aircraftCount)
    .slice(0, 10);
}

// Calculate "interesting" score and get top 10
function calculateMostInteresting(aircraftList) {
  // Calculate type frequency for rarity scoring
  const typeFrequency = {};
  aircraftList.forEach(aircraft => {
    const type = aircraft.aircraftType || 'Unknown';
    typeFrequency[type] = (typeFrequency[type] || 0) + 1;
  });
  const maxTypeFreq = Math.max(...Object.values(typeFrequency), 1);

  // Calculate country frequency for rarity scoring
  const countryFrequency = {};
  aircraftList.forEach(aircraft => {
    const country = (resolveCountryCode(aircraft, { hex: 'icao24' }) || 'Unknown').toLowerCase();
    countryFrequency[country] = (countryFrequency[country] || 0) + 1;
  });
  const maxCountryFreq = Math.max(...Object.values(countryFrequency), 1);

  const scored = aircraftList.map(aircraft => {
    let score = 0;

    // New aircraft: +30 points
    if (aircraft.isNew) score += 30;

    // Military: +30 points
    if (aircraft.military) score += 30;

    // Uncommon type: +30 * (1 - frequency/maxFreq)
    const typeFreq = typeFrequency[aircraft.aircraftType || 'Unknown'] || 1;
    const typeRarityScore = 30 * (1 - (typeFreq / maxTypeFreq));
    score += typeRarityScore;

    // Rare/distant country: +45 * (1 - frequency/maxFreq)
    // Higher weight as rare countries indicate long-distance visitors
    const countryCode = (resolveCountryCode(aircraft, { hex: 'icao24' }) || 'Unknown').toLowerCase();
    const countryFreq = countryFrequency[countryCode] || 1;
    const countryRarityScore = 45 * (1 - (countryFreq / maxCountryFreq));
    score += countryRarityScore;

    // Long range: bonus for distant aircraft
    if (aircraft.furthestRangeKm > 300) score += 10;
    if (aircraft.furthestRangeKm > 500) score += 10;

    return { ...aircraft, interestScore: score, isRareCountry: countryRarityScore > 25 };
  });

  return scored
    .filter(a => a.interestScore > 0)
    .sort((a, b) => b.interestScore - a.interestScore)
    .slice(0, 10);
}

// Simple airline logo component - relies on browser caching
const AirlineLogo = ({ airlineCode, airlineName, className = 'airline-logo', showTextFallback = true }) => {
  const [hasError, setHasError] = useState(false);
  // Re-render when the backend logo map arrives / changes.
  useAirlineLogoMapVersion();

  if (!airlineCode || !hasAirlineLogo(airlineCode) || hasError) {
    if (!showTextFallback) return null;
    return <span className="airline-text" title={airlineName}>{airlineCode || '-'}</span>;
  }

  const logoPath = getAirlineLogo(airlineCode, 'icon');

  return (
    <img
      src={logoPath}
      alt={airlineCode}
      className={className}
      title={airlineName || airlineCode}
      loading="lazy"
      onError={() => setHasError(true)}
    />
  );
};

// Function to get color based on altitude (0m to 13000m) - smooth gradient
const getAltitudeColor = (altitude) => {
  const maxAltitude = 13000; // meters

  // Clamp altitude to valid range
  const alt = Math.max(0, Math.min(altitude, maxAltitude));
  const normalized = alt / maxAltitude;

  // Smooth gradient: Yellow (low) -> Orange (mid) -> Red (high) -> Dark Red (very high)
  // Using cubic easing for smoother transitions
  let r, g, b;

  if (normalized < 0.33) {
    // Yellow to Orange (0-4300m)
    const t = normalized / 0.33;
    r = 255;
    g = Math.round(220 - (80 * t));  // 220 -> 140
    b = Math.round(50 * (1 - t));     // 50 -> 0
  } else if (normalized < 0.66) {
    // Orange to Red (4300-8600m)
    const t = (normalized - 0.33) / 0.33;
    r = 255;
    g = Math.round(140 - (100 * t));  // 140 -> 40
    b = 0;
  } else {
    // Red to Dark Red (8600-13000m)
    const t = (normalized - 0.66) / 0.34;
    r = Math.round(255 - (80 * t));   // 255 -> 175
    g = Math.round(40 - (40 * t));    // 40 -> 0
    b = 0;
  }

  return `rgb(${r}, ${g}, ${b})`;
};

// Map filter controller component
function TileFilterController({ brightness }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;
    const tilePane = map.getPane('tilePane');
    if (tilePane) {
      tilePane.style.filter = getMapFilter(brightness);
    }
  }, [map, brightness]);

  return null;
}

// Keeps Leaflet's idea of its own size in step with the container.
// The container height changes whenever the detail panel opens or closes (and on
// rotation / Split-View drags), and Leaflet only measures on its own resize
// event — so without this the map paints tiles for the OLD size and the rest of
// the container is left blank grey. pan:false keeps the centre put.
function MapResizeController() {
  const map = useMap();

  useEffect(() => {
    if (!map) return undefined;
    const container = map.getContainer();
    if (!container || typeof ResizeObserver === 'undefined') return undefined;

    let frame = null;
    const observer = new ResizeObserver(() => {
      // Coalesce to one invalidate per frame: a panel open fires several.
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        map.invalidateSize({ pan: false, animate: false });
      });
    });
    observer.observe(container);

    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [map]);

  return null;
}

// Component to fit map to track bounds - limit max zoom to maintain context
function FitBoundsController({ trackPoints }) {
  const map = useMap();

  useEffect(() => {
    if (trackPoints && trackPoints.length > 0) {
      const bounds = L.latLngBounds(trackPoints.map(p => [p.latitude, p.longitude]));

      // Calculate the diagonal distance of bounds in km
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const R = 6371; // Earth radius in km
      const dLat = (ne.lat - sw.lat) * Math.PI / 180;
      const dLon = (ne.lng - sw.lng) * Math.PI / 180;
      const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(sw.lat * Math.PI / 180) * Math.cos(ne.lat * Math.PI / 180) *
                Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      const diagonalKm = R * c;

      // If bounds are smaller than 50km, expand them
      const minDistanceKm = 50;
      if (diagonalKm < minDistanceKm) {
        const center = bounds.getCenter();
        // Expand by ~25km in each direction (roughly 0.225 degrees latitude)
        const expandDeg = 0.225;
        const expandedBounds = L.latLngBounds(
          [center.lat - expandDeg, center.lng - expandDeg * 1.5],
          [center.lat + expandDeg, center.lng + expandDeg * 1.5]
        );
        map.fitBounds(expandedBounds, { padding: [50, 50] });
      } else {
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
      }
    }
  }, [map, trackPoints]);

  return null;
}

// Component to recenter map on receiver when it changes
function RecenterMapController({ center, hasTrack, hasHeatmap, autoZoom = 'none' }) {
  const map = useMap();

  useEffect(() => {
    // Only recenter if there's no track displayed, no heatmap, and auto zoom is disabled
    // Auto zoom controller handles zooming when autoZoom is not 'none'
    if (!hasTrack && !hasHeatmap && autoZoom === 'none' && center && center[0] && center[1]) {
      map.setView(center, 8);
    }
  }, [map, center, hasTrack, hasHeatmap, autoZoom]);

  return null;
}

// Component to fit map to heatmap bounds when heatmap is enabled
function HeatmapBoundsController({ bounds }) {
  const map = useMap();

  useEffect(() => {
    if (bounds && bounds.length === 2) {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
    }
  }, [map, bounds]);

  return null;
}

// Component for auto zoom on History page
function AutoZoomHistoryController({ autoZoom, rangeBounds, heatmapBounds }) {
  const map = useMap();
  const lastAutoZoomRef = useRef(autoZoom);

  useEffect(() => {
    // Only zoom when auto zoom mode changes or on initial mount with a valid mode
    const modeChanged = lastAutoZoomRef.current !== autoZoom;
    lastAutoZoomRef.current = autoZoom;

    if (autoZoom === 'none') return;

    // Only perform initial zoom or when mode changes
    if (!modeChanged) return;

    if (autoZoom === 'range' && rangeBounds && rangeBounds.length === 2) {
      map.flyToBounds(rangeBounds, { duration: 0.5, padding: [30, 30], maxZoom: 10 });
    } else if (autoZoom === 'heatmap' && heatmapBounds && heatmapBounds.length === 2) {
      map.flyToBounds(heatmapBounds, { duration: 0.5, padding: [30, 30], maxZoom: 10 });
    }
  }, [map, autoZoom, rangeBounds, heatmapBounds]);

  return null;
}

// Altitude Profile Chart Component
function AltitudeProfile({ trackPoints, hoveredIndex, onHover, getAltitudeColor, unitSystem }) {
  const svgRef = useRef(null);

  if (!trackPoints || trackPoints.length < 2) return null;

  // ViewBox dimensions
  const viewWidth = 1000;
  const viewHeight = 120;
  const padding = { top: 20, right: 40, bottom: 25, left: 60 };
  const chartWidth = viewWidth - padding.left - padding.right;
  const chartHeight = viewHeight - padding.top - padding.bottom;

  // Altitude axis: always from 0 up to the flight's maximum rounded UP to the
  // next 10,000 ft (imperial) or 3,000 m (metric), so two flights can be
  // compared at a glance and a level cruise reads as a flat line, not a
  // zoomed-in wobble. Internal values stay in feet; the step is chosen in
  // the display unit and converted back.
  const altitudes = trackPoints.map(p => p.altitude || 0);
  const peakAlt = Math.max(...altitudes, 0);
  const stepFt = unitSystem === 'metric' ? 3000 / 0.3048 : 10000;
  const minAlt = 0;
  const maxAlt = Math.max(stepFt, Math.ceil(peakAlt / stepFt) * stepFt);
  const altRange = maxAlt - minAlt;
  const gridSteps = Math.round(maxAlt / stepFt);

  // Calculate cumulative distance for x-axis
  const distances = [0];
  let totalDist = 0;
  for (let i = 1; i < trackPoints.length; i++) {
    const p1 = trackPoints[i - 1];
    const p2 = trackPoints[i];
    const R = 6371;
    const dLat = (p2.latitude - p1.latitude) * Math.PI / 180;
    const dLon = (p2.longitude - p1.longitude) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(p1.latitude * Math.PI / 180) * Math.cos(p2.latitude * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    totalDist += R * c;
    distances.push(totalDist);
  }

  // Convert altitude for display
  const convertAlt = (alt) => unitSystem === 'metric' ? Math.round(alt * 0.3048) : Math.round(alt);
  const altUnit = unitSystem === 'metric' ? 'm' : 'ft';
  const distUnit = unitSystem === 'metric' ? 'km' : 'nm';
  const displayDist = unitSystem === 'metric' ? totalDist : totalDist * 0.539957;

  // Coordinate functions
  const getX = (i) => padding.left + (distances[i] / (totalDist || 1)) * chartWidth;
  const getY = (alt) => padding.top + chartHeight - ((alt - minAlt) / altRange) * chartHeight;

  // Handle mouse move for hover
  const handleMouseMove = (e) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const relX = (x / rect.width) * viewWidth;
    const chartX = relX - padding.left;
    const targetDist = (chartX / chartWidth) * totalDist;

    let closestIdx = 0;
    let minDiff = Infinity;
    for (let i = 0; i < distances.length; i++) {
      const diff = Math.abs(distances[i] - targetDist);
      if (diff < minDiff) {
        minDiff = diff;
        closestIdx = i;
      }
    }
    onHover(closestIdx);
  };

  const handleMouseLeave = () => onHover(null);

  return (
    <div className="altitude-profile">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${viewWidth} ${viewHeight}`}
        preserveAspectRatio="none"
        className="altitude-profile-svg"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* Grid lines */}
        <line x1={padding.left} y1={padding.top} x2={padding.left} y2={padding.top + chartHeight} stroke="#444" strokeWidth="1" />
        <line x1={padding.left} y1={padding.top + chartHeight} x2={padding.left + chartWidth} y2={padding.top + chartHeight} stroke="#444" strokeWidth="1" />

        {/* One gridline + label per 10,000 ft / 3,000 m step */}
        {Array.from({ length: gridSteps + 1 }, (_, i) => {
          const alt = i * stepFt;
          const y = getY(alt);
          const isEdge = i === 0 || i === gridSteps;
          return (
            <g key={`grid-${i}`}>
              {!isEdge && (
                <line x1={padding.left} y1={y} x2={padding.left + chartWidth} y2={y} stroke="#444" strokeWidth="0.5" strokeDasharray="3,3" />
              )}
              <text x={padding.left - 8} y={y + (i === 0 ? 0 : 4)} textAnchor="end" className="axis-label">
                {convertAlt(alt).toLocaleString()}
              </text>
            </g>
          );
        })}

        {/* Unit label */}
        <text x="15" y={padding.top + chartHeight / 2} textAnchor="middle" className="unit-label" transform={`rotate(-90, 15, ${padding.top + chartHeight / 2})`}>{altUnit}</text>

        {/* Distance labels */}
        <text x={padding.left} y={viewHeight - 5} textAnchor="start" className="axis-label">0</text>
        <text x={padding.left + chartWidth} y={viewHeight - 5} textAnchor="end" className="axis-label">{Math.round(displayDist)} {distUnit}</text>

        {/* Filled area segments under track - colored by altitude */}
        {trackPoints.slice(0, -1).map((p, i) => {
          const p2 = trackPoints[i + 1];
          const avgAlt = ((p.altitude || 0) + (p2.altitude || 0)) / 2;
          const color = getAltitudeColor(Math.round(avgAlt * 0.3048));
          const baseline = padding.top + chartHeight;
          return (
            <path
              key={`fill-${i}`}
              d={`M ${getX(i)},${baseline} L ${getX(i)},${getY(p.altitude || 0)} L ${getX(i + 1)},${getY(p2.altitude || 0)} L ${getX(i + 1)},${baseline} Z`}
              fill={color}
              fillOpacity="0.3"
            />
          );
        })}

        {/* Altitude line segments - colored by altitude */}
        {trackPoints.slice(0, -1).map((p, i) => {
          const p2 = trackPoints[i + 1];
          const avgAlt = ((p.altitude || 0) + (p2.altitude || 0)) / 2;
          const color = getAltitudeColor(Math.round(avgAlt * 0.3048));
          return (
            <line
              key={`segment-${i}`}
              x1={getX(i)}
              y1={getY(p.altitude || 0)}
              x2={getX(i + 1)}
              y2={getY(p2.altitude || 0)}
              stroke={color}
              strokeWidth="3"
              strokeLinecap="round"
            />
          );
        })}

        {/* Hover indicator */}
        {hoveredIndex !== null && hoveredIndex < trackPoints.length && (
          <>
            <line
              x1={getX(hoveredIndex)}
              y1={padding.top}
              x2={getX(hoveredIndex)}
              y2={padding.top + chartHeight}
              stroke="#fff"
              strokeWidth="1"
              strokeDasharray="4,4"
            />
            <circle
              cx={getX(hoveredIndex)}
              cy={getY(trackPoints[hoveredIndex].altitude || 0)}
              r="6"
              fill={getAltitudeColor(Math.round((trackPoints[hoveredIndex].altitude || 0) * 0.3048))}
              stroke="#fff"
              strokeWidth="2"
            />
            <text
              x={getX(hoveredIndex)}
              y={padding.top - 5}
              textAnchor="middle"
              className="hover-label"
            >
              {convertAlt(trackPoints[hoveredIndex].altitude || 0)} {altUnit}
            </text>
          </>
        )}
      </svg>
    </div>
  );
}

// Format date for display
function formatDate(date) {
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

// Format time for display (24-hour with timezone)
function formatTime(timestamp) {
  if (!timestamp) return '-';
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZoneName: 'short'
    });
  } catch (e) {
    return timestamp;
  }
}

// Format duration in seconds to human readable
function formatDuration(seconds) {
  if (!seconds) return '-';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
}

// Compact clock for the phone cards. formatTime() carries the zone suffix,
// which is too wide for a card slot and reads the same on every row anyway —
// the whole list is one day on one receiver.
function formatClockShort(timestamp) {
  if (!timestamp) return '-';
  try {
    return new Date(timestamp).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch (e) {
    return '-';
  }
}

// Phone card for the history aircraft list.
//
// Built on the SAME card chrome as the live list (AircraftCard in
// DataTable.js) — same classes, same slots — so the two lists read alike on a
// phone. Three deliberate differences:
//   * the figure is first-seen time, not altitude (History has no live
//     altitude), and it LEADS the row rather than trailing it: the list sorts
//     by first seen, so the times want to read as a column down the left edge.
//     It was also the one column the old phone layout hid outright.
//   * the status badges ride with the callsign instead of sitting out at the
//     right margin — they qualify the aircraft, so they belong next to its name.
//   * a tap opens the aircraft (photo + track list) instead of expanding the
//     card in place. History's detail sheet is the expansion.
const HistoryAircraftCard = React.memo(function HistoryAircraftCard({ aircraft, isSelected, onSelect }) {
  // The logo map arrives after first paint; this card is memoised on its props,
  // so without the subscription it would keep its "no logo" decision forever.
  useAirlineLogoMapVersion();
  const countryCode = resolveCountryCode(aircraft, { hex: 'icao24' });
  const isVehicle = isGroundVehicle(aircraft);
  const aircraftType = aircraft.aircraftType;
  const spritePos = getAircraftSpritePosition(aircraftType);
  // Fixed 22px as in the live card: the size multiplier is a map affordance,
  // and in a list it only makes every row as tall as the biggest aircraft.
  const iconSize = 22;

  const callsign = aircraft.callsign?.trim();
  const primary = callsign || aircraft.registration || aircraft.icao24?.toUpperCase() || '-';
  const secondary = callsign && aircraft.registration ? aircraft.registration : null;

  const handleTap = useCallback(() => onSelect(aircraft), [aircraft, onSelect]);

  // Operator mark, on the live map's rule (AircraftMarker.js): a military
  // aircraft gets its country's roundel, everything else its airline's logo.
  // Nothing renders when we have neither — no placeholder box — so a row
  // without a logo gives the width back to the callsign instead.
  let operatorMark = null;
  if (aircraft.military) {
    if (countryCode && hasMilitaryLogo(countryCode)) {
      operatorMark = (
        <MilitaryIcon
          countryCode={countryCode}
          country={aircraft.country}
          operatorName={aircraft.airlineName}
        />
      );
    }
  } else if (!isVehicle) {
    // Same fallback the table and the map use: the callsign carries the airline
    // when the aircraft record has no code — but never when the callsign is
    // just the registration (M-ASBA is not Malaysia Airlines).
    let airlineCode = aircraft.airlineCode;
    if (!airlineCode && callsign) {
      const extracted = airlineCodeFromCallsign(callsign, { registration: aircraft.registration });
      if (extracted && hasAirlineLogo(extracted)) airlineCode = extracted;
    }
    if (airlineCode && hasAirlineLogo(airlineCode)) {
      operatorMark = (
        <AirlineLogo
          airlineCode={airlineCode}
          airlineName={aircraft.airlineName}
          className="card-operator-logo"
          showTextFallback={false}
        />
      );
    }
  }

  const badges = (isVehicle || aircraft.isNew || aircraft.military) ? (
    <span className="card-status">
      {isVehicle && <span className="status-badge vehicle">VEH</span>}
      {aircraft.isNew && <span className="status-badge new-badge">NEW</span>}
      {aircraft.military && <span className="status-badge mil-badge">MIL</span>}
    </span>
  ) : null;

  return (
    <div
      className={`aircraft-card history-card ${aircraft.isNew ? 'new' : ''} ${isSelected ? 'selected' : ''} ${aircraft.military ? 'military' : ''}`}
      onClick={handleTap}
    >
      <div className="card-main">
        {/* First seen leads the row — it is what the list is sorted by, so the
            times read as a column down the left edge. */}
        <div className="card-metric">
          <span className="metric-value">{formatClockShort(aircraft.firstSeen)}</span>
          <span className="metric-unit">first seen</span>
        </div>

        <div className="card-flag">
          {countryCode ? (
            <span className={`flag flag-${countryCode.toLowerCase()}`} title={aircraft.country}></span>
          ) : (
            <span className="flag-placeholder">-</span>
          )}
        </div>

        {operatorMark && <div className="card-operator">{operatorMark}</div>}

        {/* Badges sit with the callsign, not out at the right margin: they
            qualify the aircraft, and the eye reads them with its name. */}
        <div className="card-callsign">
          <span className="primary-row">
            <span className="primary">{primary}</span>
            {badges}
          </span>
          {secondary && <span className="secondary">{secondary}</span>}
        </div>

        <div className="card-type">
          {isVehicle ? (
            <GroundVehicleIcon size={14} />
          ) : (
            <span
              className="aircraft-sprite"
              style={{
                width: `${iconSize}px`,
                height: `${iconSize}px`,
                backgroundImage: `url(${spriteImage})`,
                backgroundSize: `${iconSize * 8}px auto`,
                backgroundPosition: `${-(spritePos.col * iconSize)}px ${-(spritePos.row * iconSize)}px`,
                transform: 'rotate(90deg)'
              }}
            ></span>
          )}
          <span className="type-code">{aircraftType || '?'}</span>
        </div>
      </div>
    </div>
  );
});

// History table columns - using cached image components
const getHistoryColumns = (unitSystem) => {
  const units = UNIT_SYSTEMS[unitSystem] || UNIT_SYSTEMS.metric;
  return [
  {
    key: 'flag',
    label: 'Flag',
    render: (row) => {
      // Show country flag for all aircraft (civilian and military)
      const countryCode = resolveCountryCode(row, { hex: 'icao24' });
      if (!countryCode) return <span>-</span>;
      return <span className={`flag flag-${countryCode.toLowerCase()}`} title={row.country}></span>;
    }
  },
  { key: 'icao24', label: 'Hex ID' },
  { key: 'registration', label: 'Reg' },
  { key: 'callsign', label: 'Callsign', render: (row) => row.callsign || '-' },
  {
    key: 'airline',
    label: 'Operator',
    render: (row) => {
      if (isGroundVehicle(row)) {
        return '-'; // a DAA truck has no operator; its callsign is a fleet ID, not an airline
      }
      if (row.military) {
        // For military aircraft, show military icon with operator name
        const countryCode = resolveCountryCode(row, { hex: 'icao24' });
        const operatorName = row.airlineName || 'Military';
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {countryCode && <MilitaryIcon countryCode={countryCode} country={row.country} operatorName={row.airlineName} />}
            <span className="airline-text" title={operatorName}>{operatorName}</span>
          </span>
        );
      }
      // For civilian aircraft, show airline logo (not when the callsign is the registration)
      let airlineCode = row.airlineCode;
      if (!airlineCode && row.callsign) {
        const extractedCode = airlineCodeFromCallsign(row.callsign, { registration: row.registration });
        if (extractedCode && hasAirlineLogo(extractedCode)) {
          airlineCode = extractedCode;
        }
      }
      return <AirlineLogo airlineCode={airlineCode} airlineName={row.airlineName} />;
    }
  },
  {
    key: 'airlineName',
    label: 'Operator Name',
    render: (row) => <span title={row.airlineName}>{row.airlineName || '-'}</span>
  },
  {
    key: 'airlineCode',
    label: 'Airline ICAO',
    render: (row) => row.airlineCode || '-'
  },
  {
    key: 'aircraftType',
    label: 'Type',
    render: (row) => {
      const aircraftType = row.aircraftType;
      if (isGroundVehicle(row)) {
        return (
          <span className="ground-vehicle-cell" title="Ground vehicle">
            <span>{aircraftType || '-'}</span>
            <GroundVehicleIcon size={12} />
          </span>
        );
      }
      const spritePos = getAircraftSpritePosition(aircraftType);
      const sizeMultiplier = getAircraftSizeMultiplier(aircraftType);
      const baseSize = 12; // Reduced from 16 for compact table rows
      const iconSize = Math.round(baseSize * sizeMultiplier);
      const bgPosX = -(spritePos.col * iconSize);
      const bgPosY = -(spritePos.row * iconSize);

      const iconStyle = {
        display: 'inline-block',
        width: `${iconSize}px`,
        height: `${iconSize}px`,
        backgroundImage: `url(${spriteImage})`,
        backgroundSize: `${iconSize * 8}px auto`,
        backgroundPosition: `${bgPosX}px ${bgPosY}px`,
        backgroundRepeat: 'no-repeat',
        filter: 'var(--aircraft-icon-filter, brightness(0) saturate(100%) brightness(0.3))',
        verticalAlign: 'middle',
        transform: 'rotate(90deg)',
        marginLeft: '3px'
      };

      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }} title={aircraftType}>
          <span>{aircraftType || '-'}</span>
          <span style={iconStyle}></span>
        </span>
      );
    }
  },
  {
    key: 'aircraftDescription',
    label: 'Description',
    render: (row) => <span title={row.aircraftDescription}>{row.aircraftDescription || '-'}</span>
  },
  {
    key: 'countryCode',
    label: 'Country Code',
    render: (row) => {
      const countryCode = resolveCountryCode(row, { hex: 'icao24' });
      return countryCode?.toUpperCase() || '-';
    }
  },
  {
    key: 'country',
    label: 'Country',
    render: (row) => {
      const countryCode = resolveCountryCode(row, { hex: 'icao24' });
      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {countryCode && <span className={`flag flag-${countryCode.toLowerCase()}`}></span>}
          <span>{row.country || countryCode?.toUpperCase() || '-'}</span>
        </span>
      );
    }
  },
  { key: 'trackCount', label: 'Tracks', isNumeric: true },
  {
    key: 'firstSeen',
    label: 'First Seen',
    render: (row) => formatTime(row.firstSeen)
  },
  {
    key: 'lastSeen',
    label: 'Last Seen',
    render: (row) => formatTime(row.lastSeen)
  },
  {
    key: 'totalTimeTrackedSeconds',
    label: 'Duration',
    render: (row) => formatDuration(row.totalTimeTrackedSeconds)
  },
  {
    key: 'furthestRangeKm',
    label: `Max Dist (${units.distance})`,
    isNumeric: true,
    render: (row) => row.furthestRangeKm ? `${convertDistance(row.furthestRangeKm, unitSystem).toFixed(1)}` : '-'
  },
  { key: 'totalMessages', label: 'Msgs', isNumeric: true },
  {
    key: 'military',
    label: 'Mil',
    render: (row) => row.military ? 'Y' : 'N'
  },
  {
    key: 'status',
    label: 'Status',
    render: (row) => {
      if (isGroundVehicle(row)) {
        return <span className="status-badge vehicle">VEHICLE</span>;
      }
      if (row.isNew) {
        return <span className="status-badge new">NEW</span>;
      }
      if (row.isRegular) {
        return <span className="status-badge regular">REGULAR</span>;
      }
      return '-';
    }
  }
];
};

// Track list columns (shown when aircraft is selected)
// Note: startTime/endTime rendered inline with access to trackPoints for accuracy
const getTrackColumns = (unitSystem) => {
  const units = UNIT_SYSTEMS[unitSystem] || UNIT_SYSTEMS.metric;
  return [
  {
    key: 'callsign',
    label: 'Callsign',
    render: (row) => row.callsign || '-'
  },
  {
    key: 'startTime',
    label: 'Start',
    // Render handled inline to use trackPoints when available
    render: (row) => formatTime(row.startTime)
  },
  {
    key: 'endTime',
    label: 'End',
    // Render handled inline to use trackPoints when available
    render: (row) => formatTime(row.endTime)
  },
  {
    key: 'durationSeconds',
    label: 'Duration',
    render: (row) => formatDuration(row.durationSeconds)
  },
  {
    key: 'maxAltitudeFeet',
    label: `Max Alt (${units.altitude})`,
    isNumeric: true,
    render: (row) => row.maxAltitudeFeet ? `${Math.round(convertAltitude(row.maxAltitudeFeet, unitSystem)).toLocaleString()}` : '-'
  },
  {
    key: 'maxDistanceKm',
    label: `Max Dist (${units.distance})`,
    isNumeric: true,
    render: (row) => row.maxDistanceKm ? `${convertDistance(row.maxDistanceKm, unitSystem).toFixed(1)}` : '-'
  },
  {
    key: 'minDistanceKm',
    label: `Min Dist (${units.distance})`,
    isNumeric: true,
    render: (row) => row.minDistanceKm ? `${convertDistance(row.minDistanceKm, unitSystem).toFixed(1)}` : '-'
  },
  { key: 'totalPositions', label: 'Positions', isNumeric: true },
  { key: 'totalMessages', label: 'Messages', isNumeric: true }
];
};

function History({ receivers, selectedReceiverUuid: initialReceiverUuid, onReceiverChange, preselectHex, preselectDate, onPreselectConsumed }) {
  const [selectedDate, setSelectedDate] = useState(() => {
    if (preselectHex && preselectDate) return preselectDate;
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [selectedReceiverUuid, setSelectedReceiverUuid] = useState('');
  const [receiverPanelOpen, setReceiverPanelOpen] = useState(false);
  const [aircraftList, setAircraftList] = useState([]);
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const [aircraftDetails, setAircraftDetails] = useState(null); // Detailed info from tracks API
  const [aircraftTracks, setAircraftTracks] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [trackPoints, setTrackPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'firstSeen', direction: 'asc' });
  const [aircraftFilter, setAircraftFilter] = useState('all'); // 'all', 'new', 'notRegular', 'military', 'noType', 'vehicles'
  // The list is unmounted while an aircraft's details are shown, so remember
  // where it was scrolled to and put it back when the details close.
  const listScrollRef = useRef(null);
  const savedListScrollTop = useRef(0);
  const [flightSearch, setFlightSearch] = useState('');

  // Pre-select hex handed in from another page (e.g. Predictions hex
  // column click). Apply once and clear so subsequent navigations to
  // History don't keep re-applying a stale filter.
  useEffect(() => {
    if (preselectHex) {
      setFlightSearch(preselectHex);
      if (preselectDate) setSelectedDate(preselectDate);
      if (typeof onPreselectConsumed === 'function') onPreselectConsumed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectHex]);
  const [maxDistancePoints, setMaxDistancePoints] = useState([]); // Daily range outline data
  const [showHeatmap, setShowHeatmap] = useState(false); // Heatmap visibility toggle
  const [heatmapSectors, setHeatmapSectors] = useState([]); // Heatmap sector data
  const [hoveredPointIndex, setHoveredPointIndex] = useState(null); // For altitude profile sync
  const [autoZoom, setAutoZoom] = useState('none'); // Auto zoom: 'none', 'range', 'heatmap'

  // Mobile layout state
  const [mobileContentTab, setMobileContentTab] = useState('list'); // 'list', 'statistics'
  const [isMobileLayout, setIsMobileLayout] = useState(() => window.innerWidth <= 1024);
  // Narrower than isMobileLayout: the aircraft list becomes a card stack, as
  // the live list does at the same 768px. A tablet still gets the full table.
  const [isPhoneCards, setIsPhoneCards] = useState(() => window.innerWidth <= 768);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  // Refs for dropdown click-outside handling
  const filterDropdownRef = useRef(null);

  // Get settings from context (single interval at app level)
  const { mapSettings, colourSettings, unitsSettings } = useSettings();
  const showGroundVehicles = colourSettings.showGroundVehicles !== false;
  const unitSystem = unitsSettings.units;

  // Get column configuration from context
  const { columnConfig } = useColumnConfig();
  const historyAircraftConfig = columnConfig?.tables?.historyAircraft;
  const historyTracksConfig = columnConfig?.tables?.historyTracks;

  // Generate columns based on unit system and column configuration
  const historyColumns = useMemo(() => {
    const allColumns = getHistoryColumns(unitSystem);
    if (historyAircraftConfig) {
      return filterAndOrderColumns(allColumns, historyAircraftConfig);
    }
    return allColumns;
  }, [unitSystem, historyAircraftConfig]);

  const trackColumns = useMemo(() => {
    const allColumns = getTrackColumns(unitSystem);
    if (historyTracksConfig) {
      return filterAndOrderColumns(allColumns, historyTracksConfig);
    }
    return allColumns;
  }, [unitSystem, historyTracksConfig]);

  // Get units for inline display
  const units = UNIT_SYSTEMS[unitSystem] || UNIT_SYSTEMS.metric;

  // Mobile layout detection
  useEffect(() => {
    const checkLayout = () => {
      setIsMobileLayout(window.innerWidth <= 1024);
      setIsPhoneCards(window.innerWidth <= 768);
    };
    window.addEventListener('resize', checkLayout);
    window.addEventListener('orientationchange', checkLayout);
    return () => {
      window.removeEventListener('resize', checkLayout);
      window.removeEventListener('orientationchange', checkLayout);
    };
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Follow the app-wide receiver selection (the last one picked on any page)
  // when it names a receiver this user can see.
  useEffect(() => {
    if (initialReceiverUuid && initialReceiverUuid !== 'ALL_RECEIVERS'
        && receivers.some(r => r.receiverUuid === initialReceiverUuid)) {
      setSelectedReceiverUuid(initialReceiverUuid);
      return;
    }

    if (selectedReceiverUuid) return; // Already selected, don't override

    // If ALL_RECEIVERS or no selection, find first active receiver
    if (receivers.length > 0) {
      // Try to find first active receiver
      const activeReceiver = receivers.find(r => r.status === 'ACTIVE' || r.isActive);
      if (activeReceiver) {
        setSelectedReceiverUuid(activeReceiver.receiverUuid);
      } else {
        // Fall back to first receiver
        setSelectedReceiverUuid(receivers[0].receiverUuid);
      }
    }
  }, [receivers, initialReceiverUuid, selectedReceiverUuid]);

  // Fetch aircraft list when receiver or date changes
  useEffect(() => {
    if (!selectedReceiverUuid) {
      setAircraftList([]);
      return;
    }

    const fetchAircraftList = async () => {
      setLoading(true);
      setError(null);
      setSelectedAircraft(null);
      setAircraftTracks([]);
      setSelectedTrack(null);
      setTrackPoints([]);

      try {
        const token = getAccessToken();
        const response = await getTrackedAircraftList(selectedReceiverUuid, selectedDate, token);

        if (response.header?.apiStatus === 'OK' || response.header?.apiStatus === 'SUCCESS') {
          setAircraftList(response.aircraft || []);
        } else {
          setError(response.header?.errors?.[0]?.message || 'Failed to fetch aircraft list');
          setAircraftList([]);
        }
      } catch (err) {
        console.error('Error fetching aircraft list:', err);
        setError(err.message || 'Network error');
        setAircraftList([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAircraftList();
  }, [selectedReceiverUuid, selectedDate]);

  // Fetch max distance points for daily range outline when receiver or date changes
  useEffect(() => {
    if (!selectedReceiverUuid) {
      setMaxDistancePoints([]);
      return;
    }

    const fetchMaxDistancePoints = async () => {
      try {
        const token = getAccessToken();
        // consolidated=false to get all points for the outline
        const response = await getMaxDistancePoints(selectedReceiverUuid, selectedDate, false, false, token);

        if (response.header?.apiStatus === 'OK' || response.header?.apiStatus === 'SUCCESS') {
          setMaxDistancePoints(response.points || []);
        } else {
          console.error('Failed to fetch max distance points:', response.header?.errors);
          setMaxDistancePoints([]);
        }
      } catch (err) {
        console.error('Error fetching max distance points:', err);
        setMaxDistancePoints([]);
      }
    };

    fetchMaxDistancePoints();
  }, [selectedReceiverUuid, selectedDate]);

  // Fetch heatmap data when enabled and receiver/date changes
  useEffect(() => {
    if (!selectedReceiverUuid || !showHeatmap) {
      setHeatmapSectors([]);
      return;
    }

    const fetchHeatmapData = async () => {
      try {
        const token = getAccessToken();
        const response = await getReceiverHeatmap(selectedReceiverUuid, selectedDate, unitSystem, token);

        if (response.header?.apiStatus === 'OK' || response.header?.apiStatus === 'SUCCESS') {
          setHeatmapSectors(response.sectors || []);
        } else {
          console.error('Failed to fetch heatmap data:', response.header?.errors);
          setHeatmapSectors([]);
        }
      } catch (err) {
        console.error('Error fetching heatmap data:', err);
        setHeatmapSectors([]);
      }
    };

    fetchHeatmapData();
  }, [selectedReceiverUuid, selectedDate, showHeatmap, unitSystem]);

  // Handle track selection - fetch track points
  const handleTrackSelect = useCallback(async (track) => {
    if (!track) {
      setSelectedTrack(null);
      setTrackPoints([]);
      return;
    }

    // If same track clicked, deselect
    if (selectedTrack?.trackId === track.trackId) {
      setSelectedTrack(null);
      setTrackPoints([]);
      return;
    }

    setSelectedTrack(track);

    try {
      const token = getAccessToken();
      const response = await getTrackPoints(track.trackId, 5, token);

      if (response.header?.apiStatus === 'OK' || response.header?.apiStatus === 'SUCCESS') {
        setTrackPoints(response.points || []);
      } else {
        console.error('Failed to fetch track points:', response.header?.errors);
        setTrackPoints([]);
      }
    } catch (err) {
      console.error('Error fetching track points:', err);
      setTrackPoints([]);
    }
  }, [selectedTrack]);

  // A type saved from the photo (DetectedAircraftType) lands in the aircraft
  // table on the server; reflect it here without a refetch so the list row,
  // the details header and the sprite all change immediately.
  const applyAircraftTypeLocally = useCallback(({ icao24, aircraftType, aircraftDescription }) => {
    const patch = (a) => (a && a.icao24 === icao24 ? { ...a, aircraftType, aircraftDescription } : a);
    setAircraftList(prev => prev.map(patch));
    setSelectedAircraft(prev => patch(prev));
    setAircraftDetails(prev => patch(prev));
  }, []);

  // Handle aircraft selection - fetch tracks
  const handleAircraftSelect = useCallback(async (aircraft) => {
    if (!aircraft) {
      setSelectedAircraft(null);
      setAircraftDetails(null);
      setAircraftTracks([]);
      setSelectedTrack(null);
      setTrackPoints([]);
      return;
    }

    // If same aircraft clicked, deselect
    if (selectedAircraft?.icao24 === aircraft.icao24) {
      setSelectedAircraft(null);
      setAircraftDetails(null);
      setAircraftTracks([]);
      setSelectedTrack(null);
      setTrackPoints([]);
      return;
    }

    if (listScrollRef.current) savedListScrollTop.current = listScrollRef.current.scrollTop;
    setSelectedAircraft(aircraft);
    setAircraftDetails(null);
    setSelectedTrack(null);
    setTrackPoints([]);

    try {
      const token = getAccessToken();
      const response = await getAircraftTracks(aircraft.icao24, selectedReceiverUuid, selectedDate, token);

      if (response.header?.apiStatus === 'OK' || response.header?.apiStatus === 'SUCCESS') {
        const tracks = response.tracks || [];
        setAircraftTracks(tracks);

        // Store detailed aircraft info from tracks response
        // The aircraft details are at the root level of the response
        const aircraftInfo = {
          icao24: response.icao24,
          registration: response.registration,
          callsign: response.callsign,
          aircraftType: response.aircraftType,
          airlineCode: response.airlineCode,
          airlineName: response.airlineName,
          country: response.country,
          countryCode: response.countryCode,
          aircraftDescription: response.aircraftDescription,
          photo: response.photo,
          isNew: response.isNew,
          isRegular: response.isRegular,
        };
        setAircraftDetails(aircraftInfo);

        // Auto-select first track if only one exists
        if (tracks.length === 1) {
          handleTrackSelect(tracks[0]);
        }
      } else {
        console.error('Failed to fetch tracks:', response.header?.errors);
        setAircraftTracks([]);
      }
    } catch (err) {
      console.error('Error fetching aircraft tracks:', err);
      setAircraftTracks([]);
    }
  }, [selectedAircraft, selectedReceiverUuid, selectedDate, handleTrackSelect]);

  // A ground vehicle is not an aircraft. It shows under the Vehicles filter and
  // nowhere else — not in All, not in New, not in the counts, not in the stats.
  // (Settings → "Ground vehicles: Hidden" removes them from that filter too.)
  const aircraftOnly = useMemo(
    () => aircraftList.filter(a => !isGroundVehicle(a)),
    [aircraftList]
  );
  const vehiclesOnly = useMemo(
    () => (showGroundVehicles ? aircraftList.filter(a => isGroundVehicle(a)) : []),
    [aircraftList, showGroundVehicles]
  );

  // Counted once, not re-filtered per filter control on every render.
  const listCounts = useMemo(() => ({
    all: aircraftOnly.length,
    isNew: aircraftOnly.filter(a => a.isNew).length,
    military: aircraftOnly.filter(a => a.military).length,
    noType: aircraftOnly.filter(a => !a.aircraftType).length,
    vehicles: vehiclesOnly.length
  }), [aircraftOnly, vehiclesOnly]);

  // Filter and sort aircraft list
  const filteredAndSortedAircraftList = useMemo(() => {
    // First apply category filter
    let filtered = aircraftOnly;
    if (aircraftFilter === 'new') {
      filtered = aircraftOnly.filter(a => a.isNew);
    } else if (aircraftFilter === 'notRegular') {
      filtered = aircraftOnly.filter(a => !a.isRegular);
    } else if (aircraftFilter === 'military') {
      filtered = aircraftOnly.filter(a => a.military);
    } else if (aircraftFilter === 'noType') {
      filtered = aircraftOnly.filter(a => !a.aircraftType);
    } else if (aircraftFilter === 'vehicles') {
      filtered = vehiclesOnly;
    }

    // Then apply flight/registration/hex text search
    const q = flightSearch.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter(a => {
        const cs = (a.callsign || '').toLowerCase();
        const reg = (a.registration || '').toLowerCase();
        const hex = (a.icao24 || '').toLowerCase();
        return cs.includes(q) || reg.includes(q) || hex.includes(q);
      });
    }

    // Then sort
    if (!sortConfig.key || filtered.length === 0) {
      return filtered;
    }

    // Blank cells (null / '') sort as the smallest value, so the direction
    // toggle moves them: ascending puts "no type" rows at the top — the
    // natural way to work through aircraft that still need a type — and
    // descending puts them at the bottom. Previously they were pinned to the
    // bottom whichever way the column was sorted.
    const isBlank = (v) => v == null || v === '';
    return [...filtered].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      const aBlank = isBlank(aVal);
      const bBlank = isBlank(bVal);

      let comparison = 0;
      if (aBlank && bBlank) {
        comparison = 0;
      } else if (aBlank) {
        comparison = -1;
      } else if (bBlank) {
        comparison = 1;
      } else if (typeof aVal === 'string' && typeof bVal === 'string') {
        comparison = aVal.localeCompare(bVal);
      } else {
        comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      }
      if (comparison === 0) {
        // Stable tiebreak so equal rows do not shuffle between refreshes.
        comparison = (a.icao24 || '').localeCompare(b.icao24 || '');
      }

      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [aircraftOnly, vehiclesOnly, sortConfig, aircraftFilter, flightSearch]);

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return ' ↕';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  // Get selected receiver for map center
  const selectedReceiver = receivers.find(r => r.receiverUuid === selectedReceiverUuid);
  const mapCenter = selectedReceiver
    ? [selectedReceiver.latitude, selectedReceiver.longitude]
    : [51.5074, -0.1278]; // Default to London

  // A pick here also becomes the app-wide selection, so Live and the next visit
  // to History open on the receiver chosen last.
  const handleReceiverPick = (receiverName) => {
    setReceiverPanelOpen(false);
    const receiver = receivers.find(r => r.receiverName === receiverName);
    if (!receiver) return;
    setSelectedReceiverUuid(receiver.receiverUuid);
    if (onReceiverChange) onReceiverChange(receiverName);
  };

  // Memoize receiver icon to prevent flashing on re-renders
  const receiverIcon = useMemo(() => {
    return L.divIcon({
      html: `<img src="/antenna.svg" style="width: 24px; height: 24px;" />`,
      className: 'receiver-marker',
      iconSize: [24, 24],
      iconAnchor: [12, 12]
    });
  }, []);

  const tileConfig = getTileLayerConfig(mapSettings.style);

  // Calculate summary statistics from aircraft list
  // Over aircraftOnly, not the raw list: these are the day's AIRCRAFT figures,
  // and the HUD strip above the map has to agree with the list under it.
  const stats = useMemo(() => {
    if (aircraftOnly.length === 0) {
      return { totalAircraft: 0, totalMessages: 0, newAircraft: 0, regularAircraft: 0, maxDistance: 0 };
    }

    let totalMessages = 0;
    let newAircraft = 0;
    let regularAircraft = 0;
    let maxDistance = 0;

    aircraftOnly.forEach(aircraft => {
      totalMessages += aircraft.totalMessages || 0;
      if (aircraft.isNew) newAircraft++;
      if (aircraft.isRegular) regularAircraft++;
      if (aircraft.furthestRangeKm && aircraft.furthestRangeKm > maxDistance) {
        maxDistance = aircraft.furthestRangeKm;
      }
    });

    return {
      totalAircraft: aircraftOnly.length,
      totalMessages,
      newAircraft,
      regularAircraft,
      maxDistance
    };
  }, [aircraftOnly]);

  // Calculate window statistics for candlestick chart
  const windowStats = useMemo(() =>
    calculateWindowStats(aircraftOnly, maxDistancePoints),
    [aircraftOnly, maxDistancePoints]
  );

  // Calculate top 10 summaries
  const topAirlines = useMemo(() => calculateTopAirlines(aircraftOnly), [aircraftOnly]);
  const topTypes = useMemo(() => calculateTopTypes(aircraftOnly), [aircraftOnly]);
  const mostInteresting = useMemo(() => calculateMostInteresting(aircraftOnly), [aircraftOnly]);

  // Create altitude-colored track segments with curtain data for 3D effect
  const trackSegments = useMemo(() => {
    if (!trackPoints || trackPoints.length < 2) return [];

    const segments = [];
    for (let i = 0; i < trackPoints.length - 1; i++) {
      const p1 = trackPoints[i];
      const p2 = trackPoints[i + 1];

      // Convert altitude from feet to meters for color calculation
      const alt1Meters = Math.round((p1.altitude || 0) * 0.3048);
      const alt2Meters = Math.round((p2.altitude || 0) * 0.3048);
      const avgAltitude = (alt1Meters + alt2Meters) / 2;
      const avgAltitudeFeet = ((p1.altitude || 0) + (p2.altitude || 0)) / 2;

      // Curtain offset based on altitude (higher = taller curtain)
      // Creates a polygon extending down-right from the track to simulate 3D
      // Offset in degrees: min 0.016 (~1.8km), max 0.1 (~11km) at 45000ft
      const curtainOffset = Math.min(avgAltitudeFeet / 45000, 1) * 0.084 + 0.016;

      segments.push({
        positions: [[p1.latitude, p1.longitude], [p2.latitude, p2.longitude]],
        // Curtain polygon: track line + offset line below (4 points forming quadrilateral)
        curtainPositions: [
          [p1.latitude, p1.longitude],
          [p2.latitude, p2.longitude],
          [p2.latitude - curtainOffset, p2.longitude + curtainOffset],
          [p1.latitude - curtainOffset, p1.longitude + curtainOffset]
        ],
        color: getAltitudeColor(avgAltitude),
        curtainOffset
      });
    }
    return segments;
  }, [trackPoints]);

  // Create daily range outline using actual lat/lon positions (same as Live page)
  // Sort by bearing and connect points with straight lines
  const dailyRangePolyline = useMemo(() => {
    if (!maxDistancePoints || maxDistancePoints.length === 0) return null;
    if (colourSettings.showRangeOutline === false) return null;

    // Filter points that have valid lat/lon
    const validPoints = maxDistancePoints.filter(
      point => point.latitude != null && point.longitude != null
    );

    if (validPoints.length === 0) return null;

    // Sort by bearing to ensure proper line connection (same as Live page)
    const sortedPoints = [...validPoints].sort((a, b) => (a.bearing || 0) - (b.bearing || 0));

    // Create positions array from actual lat/lon
    const positions = sortedPoints.map(point => [point.latitude, point.longitude]);

    // Close the loop by adding the first point at the end
    if (positions.length > 0) {
      positions.push(positions[0]);
    }

    return {
      positions,
      color: colourSettings.maxDistanceOutline || '#1A5F5F'
    };
  }, [maxDistancePoints, colourSettings]);

  // Create heatmap sector polygons with transparency based on position count
  const heatmapPolygons = useMemo(() => {
    if (!showHeatmap || !heatmapSectors || heatmapSectors.length === 0 || !selectedReceiver) {
      return [];
    }

    // Find max position count for normalization
    const maxCount = Math.max(...heatmapSectors.map(s => s.positionCount || 0), 1);

    // Helper function to calculate point at bearing/distance from receiver
    const calculatePoint = (lat, lon, bearingDeg, distanceKm) => {
      const R = 6371; // Earth's radius in km
      const bearing = bearingDeg * Math.PI / 180;
      const lat1 = lat * Math.PI / 180;
      const lon1 = lon * Math.PI / 180;
      const d = distanceKm / R;

      const lat2 = Math.asin(
        Math.sin(lat1) * Math.cos(d) +
        Math.cos(lat1) * Math.sin(d) * Math.cos(bearing)
      );
      const lon2 = lon1 + Math.atan2(
        Math.sin(bearing) * Math.sin(d) * Math.cos(lat1),
        Math.cos(d) - Math.sin(lat1) * Math.sin(lat2)
      );

      return [lat2 * 180 / Math.PI, lon2 * 180 / Math.PI];
    };

    const receiverLat = selectedReceiver.latitude;
    const receiverLon = selectedReceiver.longitude;
    const sectorWidth = 15; // degrees
    const distanceStep = 50; // km

    return heatmapSectors.map((sector, index) => {
      const bearingStart = sector.bearingStart ?? 0;
      const bearingEnd = bearingStart + sectorWidth;
      const distanceStart = sector.distanceStart ?? 0;
      const distanceEnd = distanceStart + distanceStep;

      // Create wedge polygon points
      const points = [];

      // Inner arc (from bearingStart to bearingEnd at distanceStart)
      if (distanceStart === 0) {
        // For innermost sectors, start from receiver location
        points.push([receiverLat, receiverLon]);
      } else {
        // Add inner arc points
        for (let b = bearingStart; b <= bearingEnd; b += 5) {
          points.push(calculatePoint(receiverLat, receiverLon, b, distanceStart));
        }
      }

      // Outer arc (from bearingEnd to bearingStart at distanceEnd)
      for (let b = bearingEnd; b >= bearingStart; b -= 5) {
        points.push(calculatePoint(receiverLat, receiverLon, b, distanceEnd));
      }

      // Close the polygon
      if (distanceStart === 0) {
        points.push([receiverLat, receiverLon]);
      }

      // Calculate opacity based on position count (0.15 to 0.75)
      // Using logarithmic scale for better visual distribution
      const normalizedCount = (sector.positionCount || 0) / maxCount;
      const logNormalized = Math.log10(1 + normalizedCount * 9) / Math.log10(10); // Log scale 0-1
      const opacity = 0.15 + logNormalized * 0.6;

      // Aviotrack orange color (#E55A25) with transparency gradient
      const orangeColor = '#E55A25';

      return {
        key: `heatmap-${index}`,
        positions: points,
        color: orangeColor,
        fillOpacity: opacity,
        positionCount: sector.positionCount || 0,
        bearingStart: bearingStart,
        distanceStart: distanceStart
      };
    });
  }, [showHeatmap, heatmapSectors, selectedReceiver]);

  // Calculate bounds for heatmap auto-zoom (only include sectors with visible opacity)
  const heatmapBounds = useMemo(() => {
    if (!showHeatmap || !heatmapPolygons || heatmapPolygons.length === 0) {
      return null;
    }

    let minLat = Infinity, maxLat = -Infinity;
    let minLon = Infinity, maxLon = -Infinity;

    // Only include sectors with meaningful opacity (> 0.15) for bounds calculation
    heatmapPolygons.filter(s => s.fillOpacity > 0.15).forEach(sector => {
      sector.positions.forEach(([lat, lon]) => {
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
        if (lon < minLon) minLon = lon;
        if (lon > maxLon) maxLon = lon;
      });
    });

    if (minLat === Infinity) return null;

    return [[minLat, minLon], [maxLat, maxLon]];
  }, [showHeatmap, heatmapPolygons]);

  // Calculate bounds for daily range outline
  const rangeBounds = useMemo(() => {
    if (!dailyRangePolyline || !dailyRangePolyline.positions || dailyRangePolyline.positions.length === 0) {
      return null;
    }

    let minLat = Infinity, maxLat = -Infinity;
    let minLon = Infinity, maxLon = -Infinity;

    dailyRangePolyline.positions.forEach(([lat, lon]) => {
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lon < minLon) minLon = lon;
      if (lon > maxLon) maxLon = lon;
    });

    if (minLat === Infinity) return null;

    return [[minLat, minLon], [maxLat, maxLon]];
  }, [dailyRangePolyline]);

  // Create aircraft icon at end of track
  // A one-point track still gets its marker: a brief long-range contact often
  // stores a single position (Greptime keeps ~1 point/min), and without the
  // marker the map centres on empty sea.
  const trackMarkers = useMemo(() => {
    if (!trackPoints || trackPoints.length === 0) return null;
    const start = trackPoints[0];
    const end = trackPoints[trackPoints.length - 1];

    // Calculate heading from last two points
    let heading = 0;
    if (trackPoints.length >= 2) {
      const secondLast = trackPoints[trackPoints.length - 2];
      const dLon = (end.longitude - secondLast.longitude) * Math.PI / 180;
      const lat1 = secondLast.latitude * Math.PI / 180;
      const lat2 = end.latitude * Math.PI / 180;
      const y = Math.sin(dLon) * Math.cos(lat2);
      const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
      heading = Math.atan2(y, x) * 180 / Math.PI;
      heading = (heading + 360) % 360;
    }
    // Use heading from track point if available
    if (end.heading !== undefined && end.heading !== null) {
      heading = end.heading;
    }

    // Aircraft icon at end point
    const aircraftType = selectedAircraft?.aircraftType;
    const spritePos = getAircraftSpritePosition(aircraftType);
    const sizeMultiplier = getAircraftSizeMultiplier(aircraftType);
    const iconSize = Math.round(32 * sizeMultiplier);
    const bgPosX = -(spritePos.col * iconSize);
    const bgPosY = -(spritePos.row * iconSize);

    const endIcon = L.divIcon({
      html: `<div style="
        width: ${iconSize}px;
        height: ${iconSize}px;
        background-image: url(${spriteImage});
        background-size: ${iconSize * 8}px auto;
        background-position: ${bgPosX}px ${bgPosY}px;
        background-repeat: no-repeat;
        transform: rotate(${heading}deg);
        filter: brightness(0) saturate(100%) invert(50%) sepia(100%) saturate(500%) hue-rotate(0deg) brightness(1.2);
      "></div>`,
      className: 'aircraft-end-marker',
      iconSize: [iconSize, iconSize],
      iconAnchor: [iconSize / 2, iconSize / 2]
    });

    return { start, end, endIcon, heading };
  }, [trackPoints, selectedAircraft]);

  // Get filter label for mobile dropdown
  const getFilterLabel = () => {
    switch (aircraftFilter) {
      case 'new': return 'New';
      case 'notRegular': return 'Interesting';
      case 'military': return 'Military';
      default: return 'All';
    }
  };

  return (
    <div className={`history-page${isMobileLayout && selectedAircraft ? ' aircraft-sheet-open' : ''}`}>
      {/* Mobile Header */}
      {isMobileLayout ? (
        <div className="history-header-mobile">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="compact-date-picker"
            max={new Date().toISOString().split('T')[0]}
          />

          {/* Phones get the filter in the list bar instead — one control, with
              counts, and one less 44px row of chrome above a short list. */}
          {!isPhoneCards && (
          <div className="filter-dropdown" ref={filterDropdownRef}>
            <button
              className={`filter-dropdown-btn${aircraftFilter !== 'all' ? ' has-filter' : ''}`}
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              onTouchEnd={(e) => {
                e.preventDefault();
                setShowFilterDropdown(!showFilterDropdown);
              }}
            >
              {getFilterLabel()} ▼
            </button>
            {showFilterDropdown && (
              <div className="filter-dropdown-menu">
                <button
                  className={aircraftFilter === 'all' ? 'active' : ''}
                  onClick={() => { setAircraftFilter('all'); setShowFilterDropdown(false); }}
                >
                  All Aircraft
                </button>
                <button
                  className={aircraftFilter === 'new' ? 'active' : ''}
                  onClick={() => { setAircraftFilter('new'); setShowFilterDropdown(false); }}
                >
                  New Only
                </button>
                <button
                  className={aircraftFilter === 'notRegular' ? 'active' : ''}
                  onClick={() => { setAircraftFilter('notRegular'); setShowFilterDropdown(false); }}
                >
                  Hide Regular
                </button>
                <button
                  className={aircraftFilter === 'military' ? 'active' : ''}
                  onClick={() => { setAircraftFilter('military'); setShowFilterDropdown(false); }}
                >
                  Military
                </button>
              </div>
            )}
          </div>
          )}
        </div>
      ) : (
        /* Desktop Header */
        <div className="history-header">
          <div className="history-controls">
            <div className="control-group">
              <label>Date:</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="date-picker"
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="control-group">
              <span className="date-display">{formatDate(new Date(selectedDate))}</span>
            </div>
          </div>
          <div className="history-filters">
            <span className="filter-label">Filter:</span>
            <button
              className={`filter-btn${aircraftFilter === 'all' ? ' active' : ''}`}
              onClick={() => setAircraftFilter('all')}
            >
              All
            </button>
            <button
              className={`filter-btn filter-btn-new${aircraftFilter === 'new' ? ' active' : ''}`}
              onClick={() => setAircraftFilter('new')}
            >
              New Only
            </button>
            <button
              className={`filter-btn filter-btn-not-regular${aircraftFilter === 'notRegular' ? ' active' : ''}`}
              onClick={() => setAircraftFilter('notRegular')}
            >
              Hide Regular
            </button>
            <button
              className={`filter-btn filter-btn-military${aircraftFilter === 'military' ? ' active' : ''}`}
              onClick={() => setAircraftFilter('military')}
            >
              Military
            </button>
            <button
              className={`filter-btn${aircraftFilter === 'noType' ? ' active' : ''}`}
              onClick={() => setAircraftFilter('noType')}
              title="Aircraft with no type on record — open one to set its type from the photo"
            >
              No Type ({aircraftOnly.filter(a => !a.aircraftType).length})
            </button>
            {showGroundVehicles && (
              <button
                className={`filter-btn${aircraftFilter === 'vehicles' ? ' active' : ''}`}
                onClick={() => setAircraftFilter('vehicles')}
                title="Airport ground vehicles — transmitters that only ever send surface positions"
              >
                Vehicles ({vehiclesOnly.length})
              </button>
            )}
            <span className="filter-separator">|</span>
            <button
              className={`filter-btn filter-btn-heatmap${showHeatmap ? ' active' : ''}`}
              onClick={() => setShowHeatmap(!showHeatmap)}
              title="Show receiver coverage heatmap"
            >
              Heatmap
            </button>
            <span className="filter-separator">|</span>
            <span className="filter-label">Auto Zoom:</span>
            <select
              className="auto-zoom-select"
              value={autoZoom}
              onChange={(e) => setAutoZoom(e.target.value)}
              title="Automatically zoom map to fit data"
            >
              <option value="none">None</option>
              <option value="range">Max Range</option>
              <option value="heatmap">Heatmap</option>
            </select>
            <span className="filter-separator">|</span>
            <input
              type="text"
              className="flight-search-input"
              placeholder="Flight / reg / hex..."
              value={flightSearch}
              onChange={(e) => setFlightSearch(e.target.value)}
              title="Filter by callsign, registration, or ICAO24 hex"
            />
            {flightSearch && (
              <button
                className="flight-search-clear"
                onClick={() => setFlightSearch('')}
                title="Clear search"
              >×</button>
            )}
          </div>
        </div>
      )}

      <div className="history-content">
        <div className="history-map">
          <MapContainer
            center={mapCenter}
            zoom={8}
            style={{ height: '100%', width: '100%' }}
          >
            <MapResizeController />
            <TileLayer
              url={tileConfig.url}
              attribution={tileConfig.attribution}
            />
            <TileFilterController brightness={mapSettings.brightness} />
            <RecenterMapController center={mapCenter} hasTrack={trackPoints.length > 0} hasHeatmap={showHeatmap && heatmapBounds} autoZoom={autoZoom} />
            <AutoZoomHistoryController autoZoom={autoZoom} rangeBounds={rangeBounds} heatmapBounds={heatmapBounds} />
            {trackPoints.length > 0 && <FitBoundsController trackPoints={trackPoints} />}
            {showHeatmap && heatmapBounds && !trackPoints.length && autoZoom === 'none' && <HeatmapBoundsController bounds={heatmapBounds} />}

            {/* Track curtain polygons - filled area under track for 3D effect */}
            {trackSegments.map((segment, index) => (
              <Polygon
                key={`track-curtain-${index}`}
                positions={segment.curtainPositions}
                pathOptions={{
                  color: segment.color,
                  fillColor: segment.color,
                  fillOpacity: 0.3,
                  weight: 1,
                  opacity: 0.5
                }}
              />
            ))}

            {/* Track polyline segments - altitude colored */}
            {trackSegments.map((segment, index) => (
              <Polyline
                key={`track-segment-${index}`}
                positions={segment.positions}
                pathOptions={{
                  color: segment.color,
                  weight: 3,
                  opacity: 0.9
                }}
              />
            ))}

            {/* Aircraft icon at end of track */}
            {trackMarkers && (
              <Marker position={[trackMarkers.end.latitude, trackMarkers.end.longitude]} icon={trackMarkers.endIcon}>
                <Tooltip direction="top" offset={[0, -8]}>
                  <div>
                    <strong>End</strong><br />
                    {formatTime(trackMarkers.end.timestamp)}<br />
                    Alt: {trackMarkers.end.altitude ? `${Math.round(convertAltitude(trackMarkers.end.altitude, unitSystem)).toLocaleString()} ${units.altitude}` : '-'}<br />
                    Hdg: {Math.round(trackMarkers.heading)}°
                  </div>
                </Tooltip>
              </Marker>
            )}

            {/* Heatmap sectors */}
            {heatmapPolygons.map(sector => (
              <Polygon
                key={sector.key}
                positions={sector.positions}
                pathOptions={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  fillColor: sector.color,
                  fillOpacity: sector.fillOpacity,
                  weight: 2,
                  opacity: 1
                }}
              >
                <Tooltip>
                  <div>
                    <strong>Bearing:</strong> {sector.bearingStart}° - {sector.bearingStart + 15}°<br />
                    <strong>Distance:</strong> {sector.distanceStart} - {sector.distanceStart + 50} km<br />
                    <strong>Positions:</strong> {sector.positionCount.toLocaleString()}
                  </div>
                </Tooltip>
              </Polygon>
            ))}

            {/* Heatmap range labels */}
            {showHeatmap && heatmapPolygons.length > 0 && selectedReceiver && (() => {
              // Get unique distance rings (round to nearest 50km for clean labels)
              const rawDistances = heatmapPolygons.map(s => Math.round((s.distanceStart + 50) / 50) * 50);
              const distances = [...new Set(rawDistances)].sort((a, b) => a - b);
              const receiverLat = selectedReceiver.latitude;
              const receiverLon = selectedReceiver.longitude;

              // Find the bearing with maximum range (use center of the sector)
              const maxRangeSector = heatmapPolygons.reduce((max, s) =>
                (s.distanceStart > (max?.distanceStart || 0)) ? s : max, null);
              const labelBearing = maxRangeSector ? maxRangeSector.bearingStart + 7.5 : 90;

              // Calculate point at bearing/distance from receiver
              const calculatePoint = (lat, lon, bearingDeg, distanceKm) => {
                const R = 6371;
                const bearing = bearingDeg * Math.PI / 180;
                const lat1 = lat * Math.PI / 180;
                const lon1 = lon * Math.PI / 180;
                const d = distanceKm / R;
                const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(bearing));
                const lon2 = lon1 + Math.atan2(Math.sin(bearing) * Math.sin(d) * Math.cos(lat1), Math.cos(d) - Math.sin(lat1) * Math.sin(lat2));
                return [lat2 * 180 / Math.PI, lon2 * 180 / Math.PI];
              };

              return distances.map(dist => {
                const pos = calculatePoint(receiverLat, receiverLon, labelBearing, dist);
                return (
                  <Marker
                    key={`range-label-${dist}`}
                    position={pos}
                    icon={L.divIcon({
                      html: `<div class="heatmap-range-label">${dist} km</div>`,
                      className: 'heatmap-range-marker',
                      iconSize: [50, 20],
                      iconAnchor: [0, 10]
                    })}
                    interactive={false}
                  />
                );
              });
            })()}

            {/* Daily range outline */}
            {dailyRangePolyline && (
              <Polyline
                positions={dailyRangePolyline.positions}
                pathOptions={{
                  color: dailyRangePolyline.color,
                  weight: 2,
                  opacity: 0.7,
                  interactive: false
                }}
              />
            )}

            {/* Receiver marker */}
            {selectedReceiver && (
              <Marker
                position={[selectedReceiver.latitude, selectedReceiver.longitude]}
                icon={receiverIcon}
              >
                <Tooltip direction="top" offset={[0, -12]}>
                  {selectedReceiver.receiverName}
                </Tooltip>
              </Marker>
            )}

            {/* Hover position marker from altitude profile */}
            {hoveredPointIndex !== null && trackPoints[hoveredPointIndex] && (
              <Marker
                position={[trackPoints[hoveredPointIndex].latitude, trackPoints[hoveredPointIndex].longitude]}
                icon={L.divIcon({
                  html: `<div class="hover-position-marker"></div>`,
                  className: 'hover-position-icon',
                  iconSize: [16, 16],
                  iconAnchor: [8, 8]
                })}
              />
            )}
          </MapContainer>

          {/* Receiver picker: the same chip and popup as the Live map. */}
          {receivers.length > 0 && (
            <div className="map-ctl-stack">
              <ReceiverChip
                receivers={receivers}
                selected={selectedReceiver ? selectedReceiver.receiverName : ''}
                open={receiverPanelOpen}
                onClick={() => setReceiverPanelOpen(open => !open)}
              />
            </div>
          )}
          {receiverPanelOpen && (
            <>
              <div className="map-filter-backdrop" onClick={() => setReceiverPanelOpen(false)} />
              <div className="map-filter-panel">
                <ReceiverOptions
                  receivers={receivers}
                  selected={selectedReceiver ? selectedReceiver.receiverName : ''}
                  showAll={false}
                  onSelect={handleReceiverPick}
                />
              </div>
            </>
          )}

          {/* Stats Overlay */}
          <HistoryStatsOverlay
            totalAircraft={stats.totalAircraft}
            totalMessages={stats.totalMessages}
            newAircraft={stats.newAircraft}
            regularAircraft={stats.regularAircraft}
            militaryCount={aircraftOnly.filter(a => a.military).length}
            maxDistance={convertDistance(stats.maxDistance, unitSystem)}
            distanceUnit={units.distance}
            loading={loading}
          />

          {/* Altitude Profile Chart. On a phone it belongs to the open track
              row instead (see the track list below), so the map stays a clean
              half of the screen. */}
          {!isPhoneCards && trackPoints.length >= 2 && (
            <AltitudeProfile
              trackPoints={trackPoints}
              hoveredIndex={hoveredPointIndex}
              onHover={setHoveredPointIndex}
              getAltitudeColor={getAltitudeColor}
              unitSystem={unitSystem}
            />
          )}
        </div>

        <div className="history-table-container">
          {/* Content Tabs - Aircraft List vs Statistics */}
          {!selectedAircraft && aircraftList.length > 0 && !loading && !error && selectedReceiverUuid && (
            <div className="history-content-tabs">
              <button
                className={`content-tab${mobileContentTab === 'list' ? ' active' : ''}`}
                onClick={() => setMobileContentTab('list')}
                onTouchEnd={(e) => { e.preventDefault(); setMobileContentTab('list'); }}
              >
                Aircraft List
              </button>
              <button
                className={`content-tab${mobileContentTab === 'statistics' ? ' active' : ''}`}
                onClick={() => setMobileContentTab('statistics')}
                onTouchEnd={(e) => { e.preventDefault(); setMobileContentTab('statistics'); }}
              >
                Statistics
              </button>
            </div>
          )}

          {loading ? (
            <div className="loading-message">Loading aircraft data...</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : !selectedReceiverUuid ? (
            <div className="no-data">Please select a receiver</div>
          ) : aircraftList.length === 0 ? (
            <div className="no-data">No aircraft tracked on this date</div>
          ) : (
            <>
              {/* Statistics Section - Chart and Summary Tables - show when Statistics tab is active */}
              {!selectedAircraft && mobileContentTab === 'statistics' && (
                <div className="statistics-section">
                  {/* Activity Chart */}
                  <div className="chart-section">
                    <h4 className="section-title">Aircraft Activity (10-min windows)</h4>
                    <ActivityChart windowStats={windowStats} unitSystem={unitSystem} />
                  </div>

                  {/* Summary Tables Row */}
                  <div className="summary-tables-row">
                      {/* Top Airlines */}
                      <div className="summary-table-container">
                        <h4 className="summary-table-title">Top Airlines</h4>
                        <table className="summary-table">
                          <thead>
                            <tr>
                              <th>Operator</th>
                              <th className="numeric-column">A/C</th>
                              <th className="numeric-column">Tracks</th>
                            </tr>
                          </thead>
                          <tbody>
                            {topAirlines.map((airline, idx) => (
                              <tr key={idx}>
                                <td>
                                  <div className="summary-cell-with-logo">
                                    <AirlineLogo
                                      airlineCode={airline.code}
                                      airlineName={airline.name}
                                      className="summary-airline-logo"
                                      showTextFallback={false}
                                    />
                                    <span className="summary-text" title={airline.name}>
                                      {airline.name?.length > 18 ? airline.name.substring(0, 18) + '...' : airline.name}
                                    </span>
                                  </div>
                                </td>
                                <td className="numeric-column">{airline.aircraftCount}</td>
                                <td className="numeric-column">{airline.trackCount}</td>
                              </tr>
                            ))}
                            {topAirlines.length === 0 && (
                              <tr><td colSpan="3" className="no-data-cell">No data</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Top Aircraft Types */}
                      <div className="summary-table-container">
                        <h4 className="summary-table-title">Top Aircraft Types</h4>
                        <table className="summary-table">
                          <thead>
                            <tr>
                              <th>Type</th>
                              <th className="numeric-column">A/C</th>
                              <th className="numeric-column">Tracks</th>
                            </tr>
                          </thead>
                          <tbody>
                            {topTypes.map((type, idx) => {
                              const spritePos = getAircraftSpritePosition(type.type);
                              const iconSize = 14; // Match airline logo size
                              const bgPosX = -(spritePos.col * iconSize);
                              const bgPosY = -(spritePos.row * iconSize);
                              const iconStyle = {
                                display: 'inline-block',
                                width: `${iconSize}px`,
                                height: `${iconSize}px`,
                                backgroundImage: `url(${spriteImage})`,
                                backgroundSize: `${iconSize * 8}px auto`,
                                backgroundPosition: `${bgPosX}px ${bgPosY}px`,
                                backgroundRepeat: 'no-repeat',
                                filter: 'var(--aircraft-icon-filter, brightness(0) saturate(100%) brightness(0.3))',
                                verticalAlign: 'middle',
                                transform: 'rotate(90deg)',
                                marginRight: '4px'
                              };
                              // Show description, clean up commas
                              const displayName = (type.description || type.type).replace(/,\s*/g, ' ');
                              return (
                                <tr key={idx}>
                                  <td title={type.type}>
                                    <div className="summary-cell-with-logo">
                                      <span style={iconStyle}></span>
                                      <span className="summary-text">{displayName}</span>
                                    </div>
                                  </td>
                                  <td className="numeric-column">{type.aircraftCount}</td>
                                  <td className="numeric-column">{type.trackCount}</td>
                                </tr>
                              );
                            })}
                            {topTypes.length === 0 && (
                              <tr><td colSpan="3" className="no-data-cell">No data</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                  {/* Most Interesting */}
                  <div className="highlights-section">
                      <div className="summary-table-container full-width">
                        <h4 className="summary-table-title">Most Interesting</h4>
                        <table className="summary-table">
                          <thead>
                            <tr>
                              <th>Aircraft</th>
                              <th>Type</th>
                              <th>Operator</th>
                              <th>Why</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mostInteresting.map((aircraft, idx) => {
                              const reasons = [];
                              if (aircraft.isNew) reasons.push('NEW');
                              if (aircraft.military) reasons.push('MIL');
                              if (aircraft.isRareCountry) reasons.push('EXOTIC');
                              if (aircraft.furthestRangeKm > 300) reasons.push('FAR');
                              const countryCode = resolveCountryCode(aircraft, { hex: 'icao24' });
                              return (
                                <tr key={idx} onClick={() => handleAircraftSelect(aircraft)} className="clickable-row">
                                  <td>
                                    <div className="summary-cell-with-logo">
                                      {countryCode && (
                                        <span
                                          className={`flag flag-${countryCode.toLowerCase()} summary-flag`}
                                          title={aircraft.country}
                                        ></span>
                                      )}
                                      <span title={aircraft.registration || aircraft.icao24}>
                                        {aircraft.registration || aircraft.icao24?.toUpperCase()}
                                      </span>
                                    </div>
                                  </td>
                                  <td>{aircraft.aircraftType || '-'}</td>
                                  <td>
                                    <span className="summary-text" title={aircraft.airlineName}>
                                      {aircraft.airlineName?.length > 12 ? aircraft.airlineName.substring(0, 12) + '...' : (aircraft.airlineName || '-')}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="interest-tags">
                                      {reasons.map((r, i) => (
                                        <span key={i} className={`interest-tag interest-tag-${r.toLowerCase()}`}>{r}</span>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                            {mostInteresting.length === 0 && (
                              <tr><td colSpan="4" className="no-data-cell">No data</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                </div>
              )}

              {/* Aircraft list table - show when List tab is active or when aircraft selected */}
              {(mobileContentTab === 'list' || selectedAircraft) && (
              <div className="table-section">
                <h3 className="table-title">
                  {selectedAircraft ? 'Aircraft Details' : 'Aircraft List'}
                </h3>
                {!selectedAircraft ? (
                  <>
                  {/* Tablet keeps the chip bar — it has the width for it. Phones
                      get the single compact bar below instead: four 44px rows of
                      chrome (two rows of chips, a search line and a sort line)
                      left about three cards visible on a 390px screen. */}
                  {isMobileLayout && !isPhoneCards && (
                    <div className="mobile-list-filters">
                      <button
                        className={`filter-btn${aircraftFilter === 'all' ? ' active' : ''}`}
                        onClick={() => setAircraftFilter('all')}
                      >All ({listCounts.all})</button>
                      <button
                        className={`filter-btn filter-btn-new${aircraftFilter === 'new' ? ' active' : ''}`}
                        onClick={() => setAircraftFilter('new')}
                      >New ({listCounts.isNew})</button>
                      <button
                        className={`filter-btn${aircraftFilter === 'notRegular' ? ' active' : ''}`}
                        onClick={() => setAircraftFilter('notRegular')}
                      >Not Regular</button>
                      <button
                        className={`filter-btn filter-btn-military${aircraftFilter === 'military' ? ' active' : ''}`}
                        onClick={() => setAircraftFilter('military')}
                      >Military ({listCounts.military})</button>
                      <button
                        className={`filter-btn${aircraftFilter === 'noType' ? ' active' : ''}`}
                        onClick={() => setAircraftFilter('noType')}
                      >No Type ({listCounts.noType})</button>
                      {showGroundVehicles && (
                        <button
                          className={`filter-btn${aircraftFilter === 'vehicles' ? ' active' : ''}`}
                          onClick={() => setAircraftFilter('vehicles')}
                        >Vehicles ({listCounts.vehicles})</button>
                      )}
                      <input
                        type="text"
                        className="flight-search-input"
                        placeholder="Flight / reg / hex..."
                        value={flightSearch}
                        onChange={(e) => setFlightSearch(e.target.value)}
                      />
                    </div>
                  )}
                  {/* Phone: the same card stack the live list uses. Tablets and
                      desktop keep the full table. */}
                  {/* Filter, sort and search on ONE row, in the live list's
                      idiom (.mobile-sort-bar). Touch targets stay at the 44px
                      accessibility floor index.css sets — the room comes from
                      having one row instead of four, not from smaller controls.
                      The filter select carries the counts the chips used to. */}
                  {isPhoneCards && (
                    <div className="mobile-sort-bar history-list-bar">
                      <select
                        className="mobile-sort-select"
                        value={aircraftFilter}
                        onChange={(e) => setAircraftFilter(e.target.value)}
                        aria-label="Filter aircraft"
                      >
                        <option value="all">All ({listCounts.all})</option>
                        <option value="new">New ({listCounts.isNew})</option>
                        <option value="notRegular">Not regular</option>
                        <option value="military">Military ({listCounts.military})</option>
                        <option value="noType">No type ({listCounts.noType})</option>
                        {showGroundVehicles && (
                          <option value="vehicles">Vehicles ({listCounts.vehicles})</option>
                        )}
                      </select>
                      <select
                        className="mobile-sort-select"
                        value={sortConfig.key}
                        onChange={(e) => setSortConfig({ key: e.target.value, direction: sortConfig.direction })}
                        aria-label="Sort aircraft"
                      >
                        <option value="firstSeen">First seen</option>
                        <option value="lastSeen">Last seen</option>
                        <option value="callsign">Callsign</option>
                        <option value="registration">Reg</option>
                        <option value="aircraftType">Type</option>
                        <option value="trackCount">Tracks</option>
                        <option value="totalTimeTrackedSeconds">Duration</option>
                        <option value="furthestRangeKm">Max dist</option>
                      </select>
                      <button
                        className="sort-direction-btn"
                        onClick={() => setSortConfig(prev => ({ ...prev, direction: prev.direction === 'asc' ? 'desc' : 'asc' }))}
                        aria-label={sortConfig.direction === 'asc' ? 'Sort ascending' : 'Sort descending'}
                      >
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </button>
                      <input
                        type="text"
                        className="flight-search-input"
                        placeholder="Search"
                        value={flightSearch}
                        onChange={(e) => setFlightSearch(e.target.value)}
                        aria-label="Search by flight, registration or hex"
                      />
                    </div>
                  )}
                  {isPhoneCards ? (
                    <div
                      className="cards-container history-cards"
                      ref={(el) => {
                        listScrollRef.current = el;
                        if (el && savedListScrollTop.current > 0) el.scrollTop = savedListScrollTop.current;
                      }}
                    >
                      {filteredAndSortedAircraftList.length === 0 ? (
                        <div className="no-data-card">No aircraft for this day</div>
                      ) : (
                        filteredAndSortedAircraftList.map(aircraft => (
                          <HistoryAircraftCard
                            key={aircraft.icao24}
                            aircraft={aircraft}
                            isSelected={selectedAircraft?.icao24 === aircraft.icao24}
                            onSelect={handleAircraftSelect}
                          />
                        ))
                      )}
                    </div>
                  ) : (
                  <div
                    className="table-scroll"
                    ref={(el) => {
                      listScrollRef.current = el;
                      // Mounting again after "← Back": restore the remembered offset.
                      if (el && savedListScrollTop.current > 0) el.scrollTop = savedListScrollTop.current;
                    }}
                  >
                    <table className="history-table">
                      <thead>
                        <tr>
                          {historyColumns.map(col => (
                            <th
                              key={col.key}
                              onClick={() => handleSort(col.key)}
                              className={`sortable${col.isNumeric ? ' numeric-column' : ''}`}
                            >
                              {col.label}{getSortIndicator(col.key)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAndSortedAircraftList.map(aircraft => (
                          <tr
                            key={aircraft.icao24}
                            className={`${aircraft.isNew ? 'new-aircraft' : ''} ${selectedAircraft?.icao24 === aircraft.icao24 ? 'selected-aircraft' : ''} ${aircraft.military ? 'military-aircraft' : ''}`}
                            onClick={() => handleAircraftSelect(aircraft)}
                          >
                            {historyColumns.map(col => (
                              <td
                                key={col.key}
                                className={col.isNumeric ? 'numeric-column' : ''}
                                data-label={col.label}
                              >
                                {col.render ? col.render(aircraft) : (aircraft[col.key] || '-')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  )}
                  </>
                ) : (
                  /* Selected aircraft details and tracks */
                  <div className="aircraft-details">
                    {/* Grab handle — visible only when this panel is a mobile
                        bottom sheet (see .aircraft-sheet-open). Tap to dismiss
                        and reveal the full map. */}
                    <div
                      className="sheet-handle"
                      onClick={() => {
                        setSelectedAircraft(null);
                        setAircraftDetails(null);
                        setAircraftTracks([]);
                        setSelectedTrack(null);
                        setTrackPoints([]);
                      }}
                      role="button"
                      aria-label="Close aircraft details"
                    />
                    {/* Use aircraftDetails if available, fall back to selectedAircraft */}
                    {(() => {
                      const aircraft = aircraftDetails || selectedAircraft;
                      // Use countryCode from backend if available, otherwise derive from hex
                      const countryCode = resolveCountryCode(aircraft, { hex: 'icao24' });
                      const photo = aircraft.photo || selectedAircraft.photo;

                      return (
                        <>
                          {/* Compact header with back button */}
                          <div className="aircraft-detail-header-compact">
                            <button
                              className="back-button-small"
                              onClick={() => {
                                setSelectedAircraft(null);
                                setAircraftDetails(null);
                                setAircraftTracks([]);
                                setSelectedTrack(null);
                                setTrackPoints([]);
                              }}
                            >
                              ← Back
                            </button>
                            <div className="aircraft-title-row">
                              <span className="aircraft-title">
                                {aircraft.callsign || aircraft.registration || aircraft.icao24}
                              </span>
                              {aircraft.callsign && aircraft.registration && (
                                <span className="aircraft-reg-small">({aircraft.registration})</span>
                              )}
                              {isGroundVehicle(aircraft) && <span className="status-badge vehicle">VEHICLE</span>}
                              {aircraft.isNew && <span className="status-badge new">NEW</span>}
                              {aircraft.isRegular && !aircraft.isNew && <span className="status-badge regular">REGULAR</span>}
                              {aircraft.military && <span className="status-badge military">MIL</span>}
                            </div>
                          </div>

                          {/* Two-column layout: info + photo */}
                          <div className="aircraft-detail-body">
                            <div className="aircraft-info-column">
                              {/* Airline with logo */}
                              {(() => {
                                // Try airlineCode first, then the callsign (never for
                                // military, never when the callsign is the registration)
                                let airlineCode = aircraft.airlineCode;
                                if (!airlineCode && aircraft.callsign) {
                                  const extractedCode = airlineCodeFromCallsign(aircraft.callsign,
                                    { registration: aircraft.registration, military: aircraft.military });
                                  if (extractedCode && hasAirlineLogo(extractedCode)) {
                                    airlineCode = extractedCode;
                                  }
                                }
                                if (!aircraft.airlineName && !airlineCode) return null;
                                return (
                                  <div className="info-line airline-line">
                                    {aircraft.military && countryCode ? (
                                      // Military operators have no airline logo: show the
                                      // country's roundel (falls back to the flag).
                                      <MilitaryIcon countryCode={countryCode} country={aircraft.country} operatorName={aircraft.airlineName} />
                                    ) : (
                                      <AirlineLogo
                                        airlineCode={airlineCode}
                                        airlineName={aircraft.airlineName}
                                        className="airline-logo-small"
                                        showTextFallback={false}
                                      />
                                    )}
                                    <span className="airline-name">{aircraft.airlineName || airlineCode}</span>
                                  </div>
                                );
                              })()}

                              {/* Aircraft type/description */}
                              {(aircraft.aircraftDescription || aircraft.aircraftType) && (
                                <div className="info-line type-line">
                                  {(aircraft.aircraftDescription || aircraft.aircraftType)?.replace(/,\s*/g, ' ')}
                                </div>
                              )}

                              {/* Key info in compact format */}
                              <div className="info-grid-compact">
                                <div className="info-item">
                                  <span className="info-key">ICAO:</span>
                                  <span className="info-val">{aircraft.icao24?.toUpperCase()}</span>
                                </div>
                                {aircraft.country && (
                                  <div className="info-item">
                                    <span className="info-key">Country:</span>
                                    <span className="info-val">
                                      {countryCode && (
                                        <span
                                          className={`flag flag-${countryCode.toLowerCase()}`}
                                          title={aircraft.country}
                                          style={{ marginRight: '4px' }}
                                        ></span>
                                      )}
                                      {aircraft.country}
                                    </span>
                                  </div>
                                )}
                                <div className="info-item">
                                  <span className="info-key">Tracks:</span>
                                  <span className="info-val">{selectedAircraft.trackCount || aircraftTracks.length}</span>
                                </div>
                                <div className="info-item">
                                  <span className="info-key">Time:</span>
                                  <span className="info-val">{formatDuration(selectedAircraft.totalTimeTrackedSeconds)}</span>
                                </div>
                                {selectedAircraft.furthestRangeKm && (
                                  <div className="info-item">
                                    <span className="info-key">Max Dist:</span>
                                    <span className="info-val">{convertDistance(selectedAircraft.furthestRangeKm, unitSystem).toFixed(1)} {units.distance}</span>
                                  </div>
                                )}
                                <div className="info-item">
                                  <span className="info-key">Messages:</span>
                                  <span className="info-val">{selectedAircraft.totalMessages?.toLocaleString()}</span>
                                </div>
                              </div>
                            </div>

                            {/* Photo column */}
                            {photo && photo.largeThumbnailUrl && (
                              <div className="aircraft-photo-column">
                                <a
                                  href={photo.imageUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="photo-link-compact"
                                >
                                  <img
                                    src={photo.largeThumbnailUrl}
                                    alt={aircraft.registration || aircraft.callsign}
                                    className="aircraft-photo-compact"
                                    loading="lazy"
                                  />
                                </a>
                                {photo.photographer && (
                                  <div className="photo-credit-small">© {photo.photographer}</div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* The photo URL names the aircraft type. When we have no
                              type for this hex, offer it — admins can write it to the
                              aircraft row in one click. */}
                          {photo && (
                            <DetectedAircraftType
                              className="detected-type-inset"
                              icao24={aircraft.icao24}
                              photoUrl={photo.imageUrl}
                              knownType={aircraft.aircraftType}
                              onSaved={applyAircraftTypeLocally}
                            />
                          )}
                        </>
                      );
                    })()}

                    {/* Track list - sorted by earliest first */}
                    <h4 className="tracks-title">Tracks ({aircraftTracks.length})</h4>
                    {aircraftTracks.length === 0 ? (
                      <div className="no-tracks">Loading tracks...</div>
                    ) : isPhoneCards ? (
                      /* Phone: one row per track, its detail opening underneath.
                         The open row IS the selected track — handleTrackSelect
                         already toggles — so the map above shows what is open
                         and there is no second piece of state to keep in step.
                         The altitude profile lives here rather than in the map,
                         which keeps the map a clean half of the screen. */
                      <div className="track-rows">
                        {[...aircraftTracks].sort((a, b) => new Date(a.startTime) - new Date(b.startTime)).map(track => {
                          const isOpen = selectedTrack?.trackId === track.trackId;
                          // Stored times on every row, open or not (see the table below).
                          const start = track.startTime;
                          const end = track.endTime;
                          // Persisted by the backend (V087) — only 7500/7600/7700 ever get here.
                          // Read through the shared util so the wording matches the Live map.
                          const emergency = getEmergencySquawk(track.emergencySquawk);
                          return (
                            <div key={track.trackId} className={`track-row-group${isOpen ? ' open' : ''}${emergency ? ' emergency' : ''}`}>
                              <button
                                className="track-row"
                                onClick={() => handleTrackSelect(track)}
                                aria-expanded={isOpen}
                                title={emergency ? `Squawked ${emergency.code} — ${emergency.description}` : undefined}
                              >
                                <span className="track-row-chevron" aria-hidden="true">▸</span>
                                <span className="track-row-times">
                                  {formatClockShort(start)}–{formatClockShort(end)}
                                </span>
                                {track.callsign && (
                                  <span className="track-row-callsign">{track.callsign}</span>
                                )}
                                {/* The squawk is the reason this row is red, so it is named on the
                                    row itself rather than hidden behind the expander. */}
                                {emergency && (
                                  <span className="track-row-emergency">{emergency.code}</span>
                                )}
                                <span className="track-row-duration">
                                  {formatDuration(track.durationSeconds)}
                                </span>
                              </button>
                              {isOpen && (
                                <div className="track-row-detail">
                                  {/* Above the grid, not a cell in it: an emergency is not one
                                      statistic among six, and the time it began is the detail a
                                      reader actually needs next. */}
                                  {emergency && (
                                    <div className="track-detail-emergency" role="note">
                                      <strong>{emergency.code} {emergency.label}</strong>
                                      <span>{emergency.description}</span>
                                      {track.emergencyAt && (
                                        <span className="track-detail-emergency-at">
                                          from {formatClockShort(track.emergencyAt)}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  <div className="info-grid-compact">
                                    <div className="info-item">
                                      <span className="info-key">Max alt:</span>
                                      <span className="info-val">
                                        {track.maxAltitudeFeet
                                          ? `${Math.round(convertAltitude(track.maxAltitudeFeet, unitSystem)).toLocaleString()} ${units.altitude}`
                                          : '-'}
                                      </span>
                                    </div>
                                    <div className="info-item">
                                      <span className="info-key">Positions:</span>
                                      <span className="info-val">{track.totalPositions ?? '-'}</span>
                                    </div>
                                    <div className="info-item">
                                      <span className="info-key">Max dist:</span>
                                      <span className="info-val">
                                        {track.maxDistanceKm
                                          ? `${convertDistance(track.maxDistanceKm, unitSystem).toFixed(1)} ${units.distance}`
                                          : '-'}
                                      </span>
                                    </div>
                                    <div className="info-item">
                                      <span className="info-key">Min dist:</span>
                                      <span className="info-val">
                                        {track.minDistanceKm
                                          ? `${convertDistance(track.minDistanceKm, unitSystem).toFixed(1)} ${units.distance}`
                                          : '-'}
                                      </span>
                                    </div>
                                    <div className="info-item">
                                      <span className="info-key">Messages:</span>
                                      <span className="info-val">{track.totalMessages?.toLocaleString() ?? '-'}</span>
                                    </div>
                                  </div>
                                  {trackPoints.length >= 2 && (
                                    <AltitudeProfile
                                      trackPoints={trackPoints}
                                      hoveredIndex={hoveredPointIndex}
                                      onHover={setHoveredPointIndex}
                                      getAltitudeColor={getAltitudeColor}
                                      unitSystem={unitSystem}
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="table-scroll tracks-table-scroll">
                        <table className="history-table tracks-table">
                          <thead>
                            <tr>
                              {trackColumns.map(col => (
                                <th key={col.key} className={col.isNumeric ? 'numeric-column' : ''}>
                                  {col.label}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {[...aircraftTracks].sort((a, b) => new Date(a.startTime) - new Date(b.startTime)).map(track => {
                              // Stored start/end on every row. Swapping in the plotted points' times for
                              // the selected row only made a track's end jump when another row was
                              // selected (QTR778, 2026-11-02: 06:53 selected, 07:44 not), and disagree
                              // with its Duration. The backend now closes a timed-out track at its
                              // last fix, so the stored end is the right one.
                              const isSelected = selectedTrack?.trackId === track.trackId;
                              const displayStartTime = track.startTime;
                              const displayEndTime = track.endTime;

                              return (
                                <tr
                                  key={track.trackId}
                                  className={isSelected ? 'selected-track' : ''}
                                  onClick={() => handleTrackSelect(track)}
                                >
                                  {/* data-label drives the per-cell heading once the
                                      phone CSS stacks each row into a card — without
                                      it the track cards were unlabelled figures. */}
                                  {trackColumns.map(col => {
                                    // Override start/end time for selected track with trackPoints data
                                    if (col.key === 'startTime') {
                                      return (
                                        <td key={col.key} className={col.isNumeric ? 'numeric-column' : ''} data-label={col.label}>
                                          {formatTime(displayStartTime)}
                                        </td>
                                      );
                                    }
                                    if (col.key === 'endTime') {
                                      return (
                                        <td key={col.key} className={col.isNumeric ? 'numeric-column' : ''} data-label={col.label}>
                                          {formatTime(displayEndTime)}
                                        </td>
                                      );
                                    }
                                    return (
                                      <td key={col.key} className={col.isNumeric ? 'numeric-column' : ''} data-label={col.label}>
                                        {col.render ? col.render(track) : (track[col.key] || '-')}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default React.memo(History);
